export const blogPosts = [
  {
    slug: 'php2shell-critical-php-vulnerability',
    title: 'PHP2Shell: Patch Nextcloud and Matomo for CVE-2026-109618 and CVE-2026-109619',
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-10-25',
    displayDate: 'October 25, 2026',
    cardFooterLabel: 'Read the advisory',
    excerpt:
      'CVE-2026-109618 and CVE-2026-109619 allow unauthenticated remote code execution in PHP. Nextcloud and Matomo services in the Ametnes catalogue are affected. Fixed service versions are available.',
    seoDescription:
      'CVE-2026-109618 and CVE-2026-109619: impact, affected Nextcloud and Matomo services in the Ametnes catalogue, and the fixed service versions to upgrade to.',
    blocks: [
      {
        type: 'lead',
        text: "CVE-2026-109618 and CVE-2026-109619 are two critical vulnerabilities in PHP's core runtime. Both allow unauthenticated remote code execution on PHP installations serving web requests, including PHP-FPM and Apache with mod_php. Patches shipped on October 21, 2026; exploitation attempts were observed before the patches were public.",
      },
      {
        type: 'paragraph',
        text: 'Nextcloud and Matomo, the two PHP applications in the Ametnes catalogue, run on affected runtimes. Fixed service versions are published: Matomo `5.14` and Nextcloud `35.0` (via `34.0`). A running service stays on its deployed version until you change it.',
      },
      {
        type: 'heading',
        text: 'The vulnerabilities',
      },
      {
        type: 'paragraph',
        text: 'Both have CVSS v4.0 scores of 9.2 and allow unauthenticated remote code execution. They are flaws in PHP itself, not in a specific application or plugin.',
      },
      {
        type: 'paragraph',
        text: 'Official notifications: [CVE-2026-109618](https://www.cve.org/CVERecord?id=CVE-2026-109618) and [CVE-2026-109619](https://www.cve.org/CVERecord?id=CVE-2026-109619).',
      },
      {
        type: 'subheading',
        text: 'CVE-2026-109618: out-of-memory corruption',
      },
      {
        type: 'paragraph',
        text: 'An out-of-memory condition during request handling can interrupt PHP while request data is only partially initialised. Request cleanup then treats that invalid state as valid. A crafted request can trigger the sequence and reach remote code execution.',
      },
      {
        type: 'subheading',
        text: 'CVE-2026-109619: use-after-free in request state',
      },
      {
        type: 'paragraph',
        text: 'Request state can remain reachable after its backing memory is freed. A later operation then uses the stale state, giving a use-after-free that can lead to remote code execution. One confirmed path uses session upload-progress, which is enabled by default in many configurations.',
      },
      {
        type: 'heading',
        text: 'Affected services and fixed versions',
      },
      {
        type: 'paragraph',
        text: 'The catalogue carries two PHP applications: Nextcloud (file sync and collaboration) and Matomo (web analytics). Both need the fixed service version.',
      },
      {
        type: 'table',
        title: 'PHP services in the Ametnes catalogue',
        columns: ['Service', 'What it runs', 'Upgrade to'],
        rows: [
          ['Nextcloud', 'File sync, sharing and external storage', '`34.0`, then `35.0`'],
          ['Matomo', 'Web analytics and tag management', '`5.14`'],
        ],
      },
      {
        type: 'paragraph',
        text: 'Matomo upgrades from `5.9` to `5.14`. Nextcloud moves from `33.0` to `34.0`, then to `35.0`. Both upgrades are in place: the service endpoint and application data are kept.',
      },
      {
        type: 'heading',
        text: 'How to upgrade',
      },
      {
        type: 'paragraph',
        text: 'The console shows the deployed **Service Version** and status, not the PHP build underneath. For the exact PHP build or image digest, open a support request with the project, service name and resource ID.',
      },
      {
        type: 'tabs',
        tabs: [
          {
            label: 'Console',
            blocks: [
              {
                type: 'list',
                ordered: true,
                items: [
                  'Sign in to the Ametnes Platform console.',
                  'Open the project, then **Services**, and select the Matomo or Nextcloud resource.',
                  'Check **Service Version**, **Status**, **Logs** and **Events**. A service that is not `READY` needs attention before the upgrade.',
                  'Take a database and volume backup before changing the version.',
                  'Choose **Edit**, select the fixed **Service Version**, choose **Update**, and wait for `READY`: `5.14` for Matomo; `35.0` for Nextcloud. Upgrade Nextcloud 33.0 to `34.0` first, then repeat to `35.0`.',
                ],
              },
            ],
          },
          {
            label: 'Terraform',
            blocks: [
              {
                type: 'paragraph',
                text: 'For a new deployment, set `kind` to a fixed version:',
              },
              {
                type: 'code',
                label: 'Terraform: fixed versions for new services',
                code: `resource "ametnes_service" "matomo" {
  # ...
  kind = "matomo:5.14"
}

resource "ametnes_service" "nextcloud" {
  # ...
  kind = "nextcloud:35.0"
}`,
              },
              {
                type: 'paragraph',
                text: 'For an existing service, change `kind` to the fixed version and run `terraform apply`. The provider upgrades the service in place; it does not destroy and recreate it. The plan shows an in-place change (`~`), not a replacement (`-/+`).',
              },
              {
                type: 'code',
                label: 'Terraform: upgrade an existing service in place',
                code: `resource "ametnes_service" "matomo" {
  # ...
  kind = "matomo:5.14" # was matomo:5.9
}`,
              },
              {
                type: 'paragraph',
                text: 'Follow the catalogue upgrade path. For Nextcloud, change `kind` to `34.0`, apply, wait for `READY`, then change it to `35.0` and apply again. A version upgrade cannot be combined with an `architecture` (tier) change in the same apply.',
              },
            ],
          },
        ],
      },
      {
        type: 'paragraph',
        text: 'After the upgrade, confirm the new **Service Version** and `READY` status, then check **Logs** and **Events** for restart loops or PHP fatal errors, and again after the first request or upload.',
      },
    ],
    cta: {
      label: 'Talk to us about patching your PHP services',
      href: 'https://cloud.ametnes.com/contact/',
      description: 'Runtime patching and upgrades for the PHP applications you run in your own environment.',
    },
  },
  {
    slug: 'private-data-lake-dremio-trino',
    title: 'Building a private data lake with Dremio and Trino',
    category: 'Data platform',
    breadcrumbLabel: 'Data platform',
    publishedAt: '2026-10-01',
    displayDate: 'October 01, 2026',
    cardFooterLabel: 'Read the blueprint',
    excerpt:
      'Dremio and Trino are complementary open-source engines for a private data lake. This post covers the architecture, where each engine fits, and how to provision both with the Ametnes Terraform provider - and why sovereignty mandates and recurring ingestion costs make the private lake the better default.',
    seoDescription:
      'Build a private data lake with Dremio and Trino. Compare the two open-source engines, map the reference architecture, provision both with the Ametnes Terraform provider, and weigh data sovereignty and public-lake ingestion costs.',
    blocks: [
      {
        type: 'lead',
        text: 'A private data lake is not a single product. It is a small set of open components that keep storage, metadata and compute inside an environment you control: object storage for the data files, a catalog for the table metadata, and one or more SQL engines for the workloads. Its value is not architectural purity for its own sake. It is the ability to query data in its private resting place when sovereignty rules forbid the copy, and to avoid the recurring ingestion bill a public lakehouse charges for every stream and backfill. Dremio and Trino are the two engines that make that stack practical. They overlap enough to share a lake and differ enough to justify running both.',
      },
      {
        type: 'heading',
        text: 'The unique value of a private lake',
      },
      {
        type: 'paragraph',
        text: 'Data localisation is moving from guidance to mandate. The EU restricts transfers of personal data, India requires payment system data to be stored in India, and [Nigeria\'s central bank has given financial institutions until January 1, 2027](/blog/nigeria-cbn-data-localisation-mandate) to process and store payment transaction data in-country. A public data lake satisfies those rules by copying data into the provider environment and then defending the transfer. A private lake removes the transfer. It queries data in its private resting place, so the data never leaves the jurisdiction, network or storage boundary where it was created.',
      },
      {
        type: 'paragraph',
        text: 'Public lakehouse platforms typically charge for ingestion, egress, storage and query volume. On some platforms ingestion is metered directly; on others it reappears as egress, API requests and repeated sync jobs. Those charges are not a one-time migration cost. They recur with every CDC stream, hourly sync and backfill, and they grow with the number of tables and analysts. A private lake removes the copy that generates them: data is queried where it lands, compute is added next to the data, and the cost profile becomes infrastructure you can plan rather than a meter that rises with adoption.',
      },
      {
        type: 'table',
        title: 'Sovereignty and cost, side by side',
        columns: ['Pressure', 'Public data lake', 'Private data lake'],
        rows: [
          ['Where data lives', 'Copied into the provider environment', 'Stays in its private resting place'],
          ['Residency proof', 'Contract, configuration and transfer review', 'Architectural - data does not cross the boundary'],
          ['Ingestion', 'Recurring ingest charges for every stream and backfill', 'No external ingestion toll; data lands locally'],
          ['Egress', 'Charged when data leaves the provider', 'No cross-perimeter egress for analytics'],
          ['Query economics', 'Per-second slots or per-TB scanned', 'Compute sized and governed on your infrastructure'],
          ['Reachability', 'Needs a path from private sources to the provider', 'Runs beside sources with no public route'],
        ],
      },
      {
        type: 'paragraph',
        text: 'That combination is the unique value proposition. The lake does not ask you to move the data to a new jurisdiction, and it does not charge you again every time the data moves. Dremio and Trino are how you get a useful query layer on top of that boundary.',
      },
      {
        type: 'heading',
        text: 'Two engines, two jobs',
      },
      {
        type: 'paragraph',
        text: 'Trino is a distributed SQL query engine. It does not own the data. Connectors read from object storage, relational databases, Kafka and other systems, and the coordinator plans the query across workers. That makes Trino the right tool for federated SQL: joining an Iceberg table on object storage to a PostgreSQL table and a stream of events without first copying everything into one warehouse.',
      },
      {
        type: 'paragraph',
        text: 'Dremio is a lakehouse platform. It catalogs datasets over open storage, exposes a semantic layer that BI tools and analysts can share, and uses reflections to accelerate repeated queries. It is built for governed self-service: define a dataset once, control who can see it, and let consumers query it without reimplementing the business logic in every dashboard.',
      },
      {
        type: 'table',
        title: 'Where each engine fits',
        columns: ['Property', 'Trino', 'Dremio'],
        rows: [
          ['Primary role', 'Federated, distributed SQL across many sources', 'Governed lakehouse, semantic layer and BI acceleration'],
          ['Data ownership', 'None - queries data where it lives', 'Catalogs datasets over open storage'],
          ['Best fit', 'Ad hoc analytics, cross-source joins, ETL and data engineering', 'BI, self-service analytics, curated datasets and reflections'],
          ['Acceleration', 'Query planning, partitioning and connector pushdown', 'Reflections, caching and a semantic layer'],
          ['Scaling model', 'Stateless coordinators and workers; add compute independently', 'Coordinator and executor tiers sized by concurrency and workload'],
          ['Lock-in profile', 'Open SQL, open connectors, open table formats', 'Open storage and table formats with a governed access layer'],
        ],
      },
      {
        type: 'paragraph',
        text: 'The value of running both is that each does the job it was designed for, against the same data, without creating a second copy of the lake.',
      },
      {
        type: 'heading',
        text: 'Why the pair works',
      },
      {
        type: 'paragraph',
        text: 'Both engines separate compute from storage. Data files live in S3-compatible object storage, table metadata lives in a catalog, and the engines are disposable compute that can be resized or replaced without moving the data. That is the property that makes a private lake economical: you invest in storage once and add query capacity when a workload needs it.',
      },
      {
        type: 'paragraph',
        text: 'Both also work with open formats. Iceberg and Parquet files are readable by either engine, and neither requires a proprietary storage layer. A table written by one workload is available to the other, and a future engine can read the same files without a migration. The lake outlives the engine choice.',
      },
      {
        type: 'paragraph',
        text: 'The operational split is equally useful. Trino gives data engineers a general-purpose SQL surface for exploration, transformation and cross-source analysis. Dremio gives analysts a curated, governed surface with acceleration for the queries that run every morning. One team does not have to choose between flexibility and a stable semantic layer.',
      },
      {
        type: 'highlight',
        title: 'What private changes operationally',
        items: [
          'Object storage stays inside your location - no egress to an external analytics SaaS',
          'Table metadata stays with the lake, in a catalog you operate and back up',
          'Query compute runs in your network, next to the sources it reads',
          'Access is enforced at your identity and network boundary, not a vendor contract',
          'Costs move from per-query meters to infrastructure you can plan and govern',
        ],
      },
      {
        type: 'heading',
        text: 'The reference architecture',
      },
      {
        type: 'paragraph',
        text: 'The [private data lake reference architecture](/resources/architectures/private-data-lake) describes the full pattern: ingestion from operational systems, streaming and web analytics; curated datasets on private object storage; Dremio and Trino as the query layer; and consumers - BI, notebooks, ML and private AI - staying inside the boundary.',
      },
      {
        type: 'table',
        title: 'The layers of a private lake',
        columns: ['Layer', 'Component', 'Responsibility'],
        rows: [
          ['Ingestion', 'Kafka, Flink, Spark and CDC jobs', 'Land operational, streaming and event data as open files'],
          ['Storage', 'S3-compatible object storage', 'Hold Parquet and Iceberg data files inside the environment'],
          ['Catalog', 'Iceberg catalog on PostgreSQL', 'Track tables, schemas and snapshots'],
          ['Federated SQL', 'Trino', 'Query the lake and external sources through connectors'],
          ['Lakehouse', 'Dremio', 'Curate datasets, expose a semantic layer and accelerate BI'],
          ['Consumers', 'BI tools, notebooks, AI and RAG services', 'Read governed data without leaving the boundary'],
        ],
      },
      {
        type: 'paragraph',
        text: 'The important line is the boundary. Ingestion, storage, metadata, compute and consumption all sit on the same side of it. Nothing in the analytics path requires the data to cross into a multi-tenant control plane.',
      },
      {
        type: 'heading',
        text: 'Provisioning Trino and Dremio with Terraform',
      },
      {
        type: 'paragraph',
        text: 'Both services are in the Ametnes catalogue. Trino is available as `trino:480`; Dremio is available as `dremio:25.2-oss`. The `ametnes_service` resource takes a project, a location, a kind and an architecture preset. Omit the `network` attribute and the control plane manages the load balancer and port allocation for you.',
      },
      {
        type: 'code',
        label: 'Provider and data sources',
        code: `terraform {
  required_providers {
    ametnes = {
      source = "ametnes.com/cloud/ametnes"
    }
  }
}

variable "token" {
  type      = string
  sensitive = true
}

variable "username" {
  type = string
}

variable "trino_admin_password" {
  type      = string
  sensitive = true
}

provider "ametnes" {
  token    = var.token
  username = var.username
}

data "ametnes_project" "project" {
  name = "Default"
}

data "ametnes_location" "location" {
  name = "Ametnes"
  code = "DSL-USE1"
}`,
      },
      {
        type: 'code',
        label: 'A Trino service for federated SQL',
        code: `resource "ametnes_service" "trino" {
  name        = "private-lake-trino"
  project     = data.ametnes_project.project.id
  location    = data.ametnes_location.location.id
  kind        = "trino:480"
  description = "Federated SQL over the private data lake"

  capacity {
    storage = 100
  }

  config = {
    architecture     = "Small"
    "admin.user"     = "admin"
    "admin.password" = var.trino_admin_password
  }

  timeouts {
    create = "60m"
    update = "2h"
    delete = "20m"
  }
}`,
      },
      {
        type: 'code',
        label: 'A Dremio service for the governed lakehouse',
        code: `resource "ametnes_service" "dremio" {
  name        = "private-lake-dremio"
  project     = data.ametnes_project.project.id
  location    = data.ametnes_location.location.id
  kind        = "dremio:25.2-oss"
  description = "Governed lakehouse, semantic layer and BI acceleration"

  capacity {
    storage = 200
  }

  config = {
    architecture = "Small"
  }

  timeouts {
    create = "60m"
    update = "2h"
    delete = "20m"
  }
}`,
      },
      {
        type: 'paragraph',
        text: 'Add the admin password as a sensitive variable rather than a literal. The architecture preset drives CPU, memory and replica counts for both services; `capacity.storage` is distributed across the components that make up the service. Start with the smallest preset that meets your concurrency target and resize when the workload proves it.',
      },
      {
        type: 'code',
        label: 'Read the allocated endpoints',
        code: `output "trino_connection" {
  value = ametnes_service.trino.connections[0]
}

output "dremio_connection" {
  value = ametnes_service.dremio.connections[0]
}`,
      },
      {
        type: 'paragraph',
        text: 'Read the `connections` attribute instead of assuming a port. The control plane allocates each service its standard port when it is free and the next free port when it is not, so the granted host and port are what clients should use.',
      },
      {
        type: 'heading',
        text: 'One lake, two engines',
      },
      {
        type: 'paragraph',
        text: 'Out of the box, each service is self-contained: the Trino service includes an Iceberg JDBC catalog on PostgreSQL and MinIO for data files, while the Dremio service includes its own object storage and metadata components. That is enough to stand up a working private lakehouse immediately.',
      },
      {
        type: 'paragraph',
        text: 'For a single shared lake, point both engines at the same S3-compatible object storage and the same Iceberg catalog during deployment. The engines remain stateless relative to the data: storage and metadata become the durable layer, and Trino or Dremio can be replaced, resized or upgraded without a data migration. This is the configuration the reference architecture assumes.',
      },
      {
        type: 'highlight',
        title: 'Before production',
        items: [
          'Size compute by concurrent queries and bytes scanned, not by user count alone',
          'Plan object storage growth separately from catalog and compute capacity',
          'Decide which datasets are curated by Dremio and which remain raw for Trino',
          'Back up the Iceberg catalog database alongside the object storage bucket',
          'Terminate TLS at the ingress and keep the service endpoints private unless public access is required',
          'Monitor query latency, queue time and storage growth before the first executive dashboard depends on them',
        ],
      },
      {
        type: 'paragraph',
        text: 'A private lake is an operating model as much as an architecture. The engines are the easy part; the durable work is the catalog, the storage lifecycle, the access model and the boundary that keeps all three together.',
      },
      {
        type: 'paragraph',
        text: 'Ametnes is a [Dremio Authorized Partner](/blog/ametnes-partners-with-dremio) and deploys Dremio, Trino and the supporting lakehouse services inside your on-premise estate or VPC. Platform teams get TLS, patching, upgrades, backups and monitoring; data teams keep custody of the lake and the semantics that make it useful.',
      },
      {
        type: 'paragraph',
        text: 'Start with the [private data lake reference architecture](/resources/architectures/private-data-lake), then provision the two engines in the location where the data already lives.',
      },
    ],
    cta: {
      label: 'Talk to us about a private data lake',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'open-source-disaster-recovery',
    title: 'Disaster recovery is where open source gets left out',
    category: 'Resilience',
    breadcrumbLabel: 'Resilience',
    publishedAt: '2026-10-05',
    displayDate: 'October 05, 2026',
    cardFooterLabel: 'Read the guide',
    excerpt:
      'Open-source databases and brokers arrive with high availability, so teams assume disaster recovery is covered. It usually is not. This post covers the DR gap for RabbitMQ and PostgreSQL, the runbook that removes guesswork, why DR must be tested quarterly, and a manual, approval-gated GitLab pipeline for failover and failback.',
    seoDescription:
      'Disaster recovery for open-source databases and message brokers: why HA is not DR, how RabbitMQ exchange federation and PostgreSQL streaming replication work, what a DR runbook must contain, why quarterly testing matters, and a manual, approval-gated GitLab pipeline for failover and failback.',
    blocks: [
      {
        type: 'lead',
        text: 'Most open-source data services ship with high availability. RabbitMQ has quorum queues and clustered brokers; PostgreSQL has streaming replication and automated failover in most distributions. It is easy to read that as disaster recovery and move on. It is not. High availability keeps a service alive when a node, pod or disk fails inside one failure domain. Disaster recovery keeps the business alive when the whole failure domain goes away - the cluster, the region, the storage layer, or the control plane that runs it. That second problem is the one open-source deployments routinely leave out.',
      },
      {
        type: 'heading',
        text: 'High availability is not disaster recovery',
      },
      {
        type: 'paragraph',
        text: 'High availability is local. It is a leader election, a replica promotion, a broker restart - automation that keeps a service responding while individual components fail. Disaster recovery is a separate copy of the system in a separate place, plus a plan to move production onto it and, eventually, to move production back.',
      },
      {
        type: 'paragraph',
        text: 'A service can be highly available and have no DR at all. Three RabbitMQ brokers in one rack are highly available against a broker crash and completely unavailable when the rack, the network or the data center goes away. A PostgreSQL primary with two streaming replicas in the same cluster survives a node failure and loses everything if the storage account or the cluster itself is deleted.',
      },
      {
        type: 'paragraph',
        text: 'With a managed cloud, part of that second problem is hidden inside the provider contract. With open source, you own the second environment, the replication link, the promotion procedure, the application repoint, and the rehearsal. The software can do all of it. The organisation often does not, because nobody put it on the roadmap.',
      },
      {
        type: 'highlight',
        title: 'Why open-source DR gets left out',
        items: [
          'High availability is mistaken for disaster recovery, so the second site is never built',
          'Every component has a different native mechanism - federation, streaming replication, snapshots - and nobody owns the cross-component plan',
          'There is no vendor to hand you a tested runbook for your topology',
          'The software is free, so the operational cost is easy to defer until after the launch',
          'The first real test happens during the first real outage',
        ],
      },
      {
        type: 'heading',
        text: 'The four words every DR plan needs',
      },
      {
        type: 'paragraph',
        text: 'Before choosing a replication technology, write down the two numbers and the two procedures. They determine whether the design is adequate, and they are the terms you will be judged on when something fails.',
      },
      {
        type: 'table',
        title: 'The vocabulary of a DR decision',
        columns: ['Term', 'What it means', 'The question it answers'],
        rows: [
          ['RPO', 'Recovery point objective: how much data you can afford to lose, measured in time', 'How much committed work may disappear?'],
          ['RTO', 'Recovery time objective: how long you can be down before service is restored', 'How long until the business can work again?'],
          ['Failover', 'Moving the workload to the standby when the primary fails', 'What exactly do we execute when the primary is gone?'],
          ['Failback', 'Returning to the primary once it is healthy again', 'How do we get back without creating a second incident?'],
        ],
      },
      {
        type: 'paragraph',
        text: 'RPO and RTO are the numbers. Failover and failback are the procedures that decide whether you meet them. A design that only discusses replication is not a DR plan; it is a transport mechanism.',
      },
      {
        type: 'heading',
        text: 'Messaging and databases need different DR',
      },
      {
        type: 'paragraph',
        text: 'A queue and a database both hold data, but they hold different kinds of data. Treating them the same produces a plan that is either unsafe for the database or unnecessarily heavy for the broker.',
      },
      {
        type: 'table',
        title: 'Two data models, two recovery procedures',
        columns: ['Property', 'Messaging (RabbitMQ)', 'Database (PostgreSQL)'],
        rows: [
          ['Nature of the data', 'Transient work in transit', 'Durable system of record'],
          ['Consumed data', 'Gone by design, not worth protecting', 'Every committed write must survive'],
          ['What must be right', 'Pending messages arrive; repeats are tolerated', 'Exact, ordered, consistent state'],
          ['Failback', 'Repoint consumers and reprocess safely', 'Rebuild the old primary from the new one'],
        ],
      },
      {
        type: 'paragraph',
        text: 'A queue is a moving window of work, not a record. Once a message is consumed it has done its job, so DR only has to protect what is still pending. That is why a RabbitMQ failback tends to show duplicates and why consumers must be idempotent. A database is the opposite: the history is the asset, and a lost or reordered write is corruption. The same DR principle produces very different procedures.',
      },
      {
        type: 'heading',
        text: 'RabbitMQ DR Ops',
      },
      {
        type: 'paragraph',
        text: 'The RabbitMQ pattern is a primary cluster that takes all production publishes and a standby cluster that mirrors the publish flow. Replication is exchange federation, configured on the standby only. Two objects on the standby are the entire DR configuration: an upstream that says where the primary is, and a policy that says which exchanges to federate.',
      },
      {
        type: 'paragraph',
        text: 'The policy is regex-based. A pattern such as `^federated[.]` federates every exchange with that prefix, including exchanges created at runtime. For an exchange to be federated it must exist on both clusters with the same name and type, and applications must publish to named exchanges - traffic through the default exchange is invisible to federation. Exchange federation replicates the live publish flow, not a pre-existing queue backlog, so the link has to be in place before the disaster, not after it.',
      },
      {
        type: 'paragraph',
        text: 'The link is one-way and self-healing: it runs on the standby, pulls from the primary, republishes to the same-named exchange locally, and reconnects automatically after a network blip or a primary restart. The primary does not know the standby exists.',
      },
      {
        type: 'paragraph',
        text: 'The management API is reached over HTTPS on 443. Only the federation upstream uses AMQPS, on port 5671.',
      },
      {
        type: 'code',
        label: 'Configure the standby: one upstream plus one exchange policy',
        code: `STANDBY="https://rabbitmq-standby.example.com"
PRIMARY_AMQPS="rabbitmq-primary.example.com"

# 1. Where the standby connects: the primary, over TLS.
curl -fsS -u "$RABBIT_USER:$RABBIT_PASS" -X PUT \\
  "$STANDBY/api/parameters/federation-upstream/%2f/primary-upstream" \\
  -H 'content-type: application/json' \\
  -d "{\\"value\\":{\\"uri\\":\\"amqps://$RABBIT_USER:$RABBIT_PASS@$PRIMARY_AMQPS:5671\\"}}"

# 2. What to replicate: every exchange matching ^federated[.]
curl -fsS -u "$RABBIT_USER:$RABBIT_PASS" -X PUT \\
  "$STANDBY/api/policies/%2f/federate-federated" \\
  -H 'content-type: application/json' \\
  -d '{"pattern":"^federated[.]","definition":{"federation-upstream":"primary-upstream"},"apply-to":"exchanges"}'`,
      },
      {
        type: 'paragraph',
        text: 'Both calls are idempotent PUTs. Re-running the configuration is safe, which matters when the runbook is executed by a different person at 3am.',
      },
      {
        type: 'code',
        label: 'Verify that the link is running',
        code: `curl -fsS -u "$RABBIT_USER:$RABBIT_PASS" \\
  "$STANDBY/api/federation-links" \\
  | jq -r '.[] | [.exchange, .status] | @tsv'`,
      },
      {
        type: 'paragraph',
        text: 'On failover, the operator confirms the primary is actually unavailable, confirms the standby is healthy, and repoints producers and consumers to the standby endpoint. The standby queues already hold everything that was published to the federated exchanges while the link was up. On failback, the restored primary re-establishes the link automatically, applications move back, and the standby drains any messages produced there during the outage. Those outage messages do not flow back to the primary on their own; drain them or run a temporary reverse Shovel for the affected queues.',
      },
      {
        type: 'paragraph',
        text: 'The non-negotiable application requirement is idempotency. Messages processed on the standby are still present on the primary after failback, so a real consumer must tolerate reprocessing. That is a documented part of the design, not an afterthought discovered during the first drill.',
      },
      {
        type: 'heading',
        text: 'PostgreSQL DR Ops',
      },
      {
        type: 'paragraph',
        text: 'Ametnes models PostgreSQL DR as two resources: a primary with `dr.role = primary` and shared replication credentials, and a standby with `dr.role = standby` and `dr.uri` pointing at the primary. The standby is a physical clone of the primary and streams asynchronously across sites. It is read-only, and there is only ever one writer. Because it is a physical clone, it inherits the primary superuser password, so `admin.password` must be the same value on both resources.',
      },
      {
        type: 'paragraph',
        text: 'The DR mechanics are resource config, not commands you run against the database. Changing a role from `standby` to `primary` makes the platform run `pg_promote()` and roll the instance; changing it back to `standby` with a new `dr.uri` makes the platform resynchronise it as a replica. Each resource keeps its own stable endpoint, so a role change does not produce a new database address; applications are repointed from the primary endpoint to the standby endpoint and back.',
      },
      {
        type: 'code',
        label: 'Terraform: declare the primary and standby resources',
        code: `resource "ametnes_service" "postgres_primary" {
  name     = "PostgresPrimary"
  project  = ametnes_project.project.id
  location = data.ametnes_location.location_a.id
  kind     = "service/postgres:17.6"
  capacity { storage = 20 }
  config = {
    "architecture"            = "Small"
    "admin.password"          = var.postgres_admin_password
    "dr.role"                 = "primary"
    "dr.replication.user"     = "repl_user"
    "dr.replication.password" = var.replication_password
  }
}

resource "ametnes_service" "postgres_standby" {
  name     = "PostgresStandby"
  project  = ametnes_project.project.id
  location = data.ametnes_location.location_b.id
  kind     = "service/postgres:17.6"
  capacity { storage = 20 }
  config = {
    "architecture"   = "Small"
    "admin.password" = var.postgres_admin_password
    "dr.role"        = "standby"
    "dr.uri"         = "postgresql://repl_user:\${var.replication_password}@\${var.postgres_primary_host}:\${var.postgres_primary_port}/postgres"
  }
}`,
      },
      {
        type: 'paragraph',
        text: 'Failover is a config update on the standby: set `dr.role` to `primary`. The platform promotes it and reports the resource as `READY`; repoint clients once it does and the endpoint accepts connections. The failed primary is not contacted, so promotion works even when the old site is completely unreachable.',
      },
      {
        type: 'code',
        label: 'API: promote the standby by updating its config',
        code: `# PUT the full resource back with one field changed.
curl -fsS -H "Authorization: Bearer $AMETNES_TOKEN" \\
  "$AMETNES_API/projects/$PROJECT_ID/resources/$POSTGRES_STANDBY_ID" \\
  | jq '.spec.config["dr.role"] = "primary"' \\
  | curl -fsS -X PUT -H "Authorization: Bearer $AMETNES_TOKEN" \\
      -H 'content-type: application/json' --data-binary @- \\
      "$AMETNES_API/projects/$PROJECT_ID/resources/$POSTGRES_STANDBY_ID"`,
      },
      {
        type: 'paragraph',
        text: 'Failback is the same mechanism in reverse. Set the old primary to `dr.role = standby` and `dr.uri` to the promoted instance endpoint. The platform resynchronises it: incremental after a clean shutdown, a full re-seed after an ungraceful failure such as a blackout. Do not send traffic to the old primary until it reports `READY` and has rejoined as a standby. The platform does not fence it, so the runbook must guarantee it never becomes a second writer.',
      },
      {
        type: 'paragraph',
        text: 'The trade-off is explicit. This pattern is asynchronous, manually promoted and has a non-zero RPO. If you need zero data loss, automatic failover or multi-region writes, use a distributed PostgreSQL-compatible database such as YugabyteDB rather than pretending a single-writer standby provides those guarantees. The full field reference and the precise guarantees are in the [PostgreSQL DR documentation](https://cloud.ametnes.com/docs/tutorials/postgres/dr/).',
      },
      {
        type: 'heading',
        text: 'The honest numbers',
      },
      {
        type: 'paragraph',
        text: 'A DR design is credible when it states its limits. These are the limits of the two patterns as Ametnes implements them.',
      },
      {
        type: 'table',
        title: 'What the two patterns actually promise',
        columns: ['Service', 'RPO', 'RTO', 'Constraint'],
        rows: [
          ['RabbitMQ exchange federation', 'Near zero while the link is up; publishes made during a link outage are lost', 'Minutes: manual repoint and verification', 'Consumers must be idempotent; replication is one-way; no queue backlog catch-up'],
          ['PostgreSQL primary/standby', 'Greater than zero: writes not yet shipped to the standby are lost', 'Manual: promote, repoint and verify', 'Asynchronous replication; the platform does not fence the old primary; failback re-seeds after an ungraceful failure'],
        ],
      },
      {
        type: 'paragraph',
        text: 'For RabbitMQ, the honest number has an application-side condition. Queue-consuming applications must be designed with idempotency in mind, so a message replayed after failover or failback is a no-op when it has already been processed - typically by deduplicating on a message ID or a business key. Replication protects pending work; it cannot make a non-idempotent consumer safe.',
      },
      {
        type: 'paragraph',
        text: 'The numbers are not the whole story. The rest is the procedure: who declares a disaster, what they check first, which command they run, and how they know it worked. That procedure is the runbook.',
      },
      {
        type: 'heading',
        text: 'The runbook is the deliverable',
      },
      {
        type: 'paragraph',
        text: 'During a real DR event, the most expensive thing is rarely the missing command. It is the argument about which command to run. Clear guidelines and a pre-agreed execution plan remove that argument before it starts. Without them, competent engineers improvise under pressure, and improvisation is how a recovery becomes a second incident.',
      },
      {
        type: 'paragraph',
        text: 'Engineers also tend to ignore automation that already exists. Not because they are careless, but because the automation is unfamiliar, its current state is unknown, or nobody has run it since it was written. The fix is not to demand more heroics. It is to make the automation the obvious path: short, ordered, versioned, rehearsed, and owned.',
      },
      {
        type: 'paragraph',
        text: 'A runbook that lives in a wiki and is never executed is documentation, not a control. The goal is an execution plan specific enough that two different engineers would make the same decision at every step.',
      },
      {
        type: 'highlight',
        title: 'What a DR runbook must contain',
        items: [
          'Trigger and authority: the condition that declares a disaster, and who is allowed to declare it',
          'Pre-checks: evidence that the primary is actually down, not just unreachable from one network',
          'Ordered actions: the failover sequence, with the expected output at each step',
          'Repoint procedure: exactly where application configuration lives and how it changes',
          'Verification: the checks that prove the standby is serving correctly',
          'Communications: who is told, when, and through which channel',
          'Abort and rollback: how to stop if the plan stops making sense',
          'Failback: a separate, slower procedure for returning to the primary',
          'Ownership and last test date: a named owner and a rehearsal cadence',
        ],
      },
      {
        type: 'paragraph',
        text: 'The runbook should also record what not to do. For RabbitMQ, do not point at the default exchange and expect replication. For PostgreSQL, do not promote both nodes and do not send traffic to the old primary before it has rejoined as a standby. Most DR incidents are caused by an action taken out of order, not by a missing tool.',
      },
      {
        type: 'heading',
        text: 'Test on a schedule, not when it matters',
      },
      {
        type: 'paragraph',
        text: 'Disaster recovery must be tested regularly. Quarterly is a sensible default for most teams, and more often for systems that change frequently or carry regulated data. A standby that has never been rehearsed is a hope, not a recovery.',
      },
      {
        type: 'paragraph',
        text: 'A test is not a tabletop exercise alone. It should include an actual failover, an actual verification, and a timed failback, in an environment that resembles production. Record the measured RPO and RTO against the target. If the numbers are worse, fix the design or the runbook before the next quarter, not after the next outage.',
      },
      {
        type: 'paragraph',
        text: 'The rehearsal also finds the undocumented dependencies: the DNS record nobody owns, the secret only one engineer can retrieve, the monitoring that still points at the old endpoint, the consumer that is not idempotent, the failback script that assumes a data directory layout the image no longer uses. Those are exactly the things a document review will miss.',
      },
      {
        type: 'table',
        title: 'A quarterly DR rehearsal',
        columns: ['Phase', 'What happens', 'Output'],
        rows: [
          ['Plan', 'Choose scope and success criteria; restate target RPO and RTO', 'A dated test plan with owners'],
          ['Execute', 'Fail over, verify, fail back under time pressure; record timings', 'Measured RPO and RTO, plus observed failures'],
          ['Review', 'Compare results to targets; list gaps and assign owners', 'A short findings record, not a novel'],
          ['Improve', 'Update the runbook and the automation; schedule the next test', 'A changed system and a new test date'],
        ],
      },
      {
        type: 'paragraph',
        text: 'Keep the record. Date, participants, what was executed, measured RPO and RTO, issues found, owner, and the change made. That record is the evidence that DR works, and it is the input to the next rehearsal.',
      },
      {
        type: 'heading',
        text: 'Remove the second-guessing: make the runbook a pipeline',
      },
      {
        type: 'paragraph',
        text: 'One of the simplest ways to stop a DR event from turning into a debate is to make the execution plan a trigger rather than a document. A GitLab pipeline can hold the exact sequence, require a human decision at each gate, and leave an audit trail of who ran what and when.',
      },
      {
        type: 'paragraph',
        text: 'Manual jobs are the mechanism. Every DR action is `when: manual`, so nothing runs on a merge or a schedule by accident. The operator gets one button, the same button every time, with the same versioned commands behind it. The pipeline does not replace judgement; it encodes the routine so judgement is spent on the situation instead of on remembering the command.',
      },
      {
        type: 'paragraph',
        text: 'Manual is not the same as approved. A manual job can be started by anyone with permission to run it. GitLab Ultimate adds protected environments and deployment approvals: only approved users can deploy to a protected environment, and an approval rule can require one or more approvers before the job starts. On lower tiers, use protected branches, protected environments and restricted CI/CD variables to approximate the control, but understand the difference.',
      },
      {
        type: 'code',
        label: 'GitLab Ultimate: put an approval gate in front of the manual job',
        code: `# Settings > CI/CD > Protected environments
#   environment: production-dr
#   allowed to deploy: maintainers
#   required approvals: 2
#
# The DR jobs below use when: manual. The protected environment is the
# approval gate: clicking Play is not enough until the configured
# approvals have been granted.`,
      },
      {
        type: 'heading',
        text: 'A GitLab pipeline for RabbitMQ and PostgreSQL DR',
      },
      {
        type: 'paragraph',
        text: 'The example below assumes four small scripts in the repository, one per action. Keeping the commands in versioned scripts means the pipeline, the runbook and the review all point at the same code. Replace the endpoints, credentials and names with your own. Credentials come from masked and protected CI variables or an external secret manager, never from the repository.',
      },
      {
        type: 'paragraph',
        text: 'The pipeline below is one definition with four action-specific runs. Set `DR_ACTION` when triggering the pipeline - `rabbitmq-failover`, `rabbitmq-failback`, `postgres-failover`, or `postgres-failback` - and only the matching job is created. Every job is `when: manual` and `allow_failure: false`, so the action is deliberate and a failure blocks the pipeline instead of passing quietly. A shared `resource_group` serializes the actions, so a failover and a failback cannot run at the same time. The protected environment is the approval gate.',
      },
      {
        type: 'code',
        label: '.gitlab-ci.yml - four manual, approval-gated DR actions',
        code: `stages:
  - dr

default:
  image: alpine:3.20
  before_script:
    - apk add --no-cache bash curl jq postgresql-client openssl

variables:
  RABBITMQ_PRIMARY_MGMT: "https://rabbitmq-primary.example.com"
  RABBITMQ_STANDBY_MGMT: "https://rabbitmq-standby.example.com"
  POSTGRES_PRIMARY_DSN: "postgresql://dr_operator@postgres-primary.example.com:5432/appdb?sslmode=require"
  POSTGRES_STANDBY_DSN: "postgresql://dr_operator@postgres-standby.example.com:5432/appdb?sslmode=require"
  AMETNES_API: "https://cloud.ametnes.com/api/c/v1"
  PROJECT_ID: "your-project-id"
  POSTGRES_PRIMARY_ID: "your-primary-resource-id"
  POSTGRES_STANDBY_ID: "your-standby-resource-id"

# AMETNES_TOKEN, PGPASSWORD and POSTGRES_PROMOTED_URI are protected CI variables.
# POSTGRES_PROMOTED_URI is the standby's replication URI, used when the old
# primary rejoins as a standby.

# Trigger with DR_ACTION set to one of:
#   rabbitmq-failover | rabbitmq-failback | postgres-failover | postgres-failback
workflow:
  rules:
    - if: '$DR_ACTION =~ /^(rabbitmq|postgres)-(failover|failback)$/'

# Credentials are masked and protected CI variables:
# RABBIT_USER, RABBIT_PASS, PGPASSWORD.
# Protect the production-dr environment and require approvals in GitLab Ultimate.

rabbitmq:failover:
  stage: dr
  when: manual
  allow_failure: false
  resource_group: dr-production
  rules:
    - if: '$DR_ACTION == "rabbitmq-failover"'
  environment:
    name: production-dr
  script:
    - ./scripts/dr/rabbitmq-failover.sh

rabbitmq:failback:
  stage: dr
  when: manual
  allow_failure: false
  resource_group: dr-production
  rules:
    - if: '$DR_ACTION == "rabbitmq-failback"'
  environment:
    name: production-dr
  script:
    - ./scripts/dr/rabbitmq-failback.sh

postgres:failover:
  stage: dr
  when: manual
  allow_failure: false
  resource_group: dr-production
  rules:
    - if: '$DR_ACTION == "postgres-failover"'
  environment:
    name: production-dr
  script:
    - ./scripts/dr/postgres-failover.sh

postgres:failback:
  stage: dr
  when: manual
  allow_failure: false
  resource_group: dr-production
  rules:
    - if: '$DR_ACTION == "postgres-failback"'
  environment:
    name: production-dr
  script:
    - ./scripts/dr/postgres-failback.sh`,
      },
      {
        type: 'paragraph',
        text: 'The scripts are where the runbook becomes executable. The RabbitMQ failover script checks the standby, records the federation link state the operator is about to rely on, repoints the application configuration, and runs a publish/consume smoke test. The failback script verifies the primary and the link, drains the standby backlog, repoints, and proves replication is live again.',
      },
      {
        type: 'code',
        label: 'scripts/dr/rabbitmq-failover.sh',
        code: `#!/usr/bin/env bash
set -euo pipefail

: "\${RABBIT_USER:?set RABBIT_USER}"
: "\${RABBIT_PASS:?set RABBIT_PASS}"
: "\${RABBITMQ_STANDBY_MGMT:?set RABBITMQ_STANDBY_MGMT}"

# 1. The standby must be healthy before it takes production traffic.
curl -fsS -u "$RABBIT_USER:$RABBIT_PASS" \\
  "$RABBITMQ_STANDBY_MGMT/api/overview" >/dev/null

# 2. Record the replication state the operator is relying on.
curl -fsS -u "$RABBIT_USER:$RABBIT_PASS" \\
  "$RABBITMQ_STANDBY_MGMT/api/federation-links" \\
  | jq -r '.[] | [.exchange, .status] | @tsv'

# 3. Repoint producers and consumers in one place, then verify.
./scripts/dr/repoint.sh rabbitmq standby
./scripts/dr/rabbitmq-smoke-test.sh standby`,
      },
      {
        type: 'code',
        label: 'scripts/dr/rabbitmq-failback.sh',
        code: `#!/usr/bin/env bash
set -euo pipefail

# 1. The primary must be healthy and the federation link running again.
./scripts/dr/rabbitmq-verify.sh primary
./scripts/dr/rabbitmq-verify-link.sh

# 2. Let the standby finish messages produced there during the outage.
./scripts/dr/rabbitmq-drain-standby.sh

# 3. Return applications to the primary and prove replication is live.
./scripts/dr/repoint.sh rabbitmq primary
./scripts/dr/rabbitmq-smoke-test.sh primary
./scripts/dr/rabbitmq-smoke-test.sh standby`,
      },
      {
        type: 'paragraph',
        text: 'The PostgreSQL scripts follow the same shape, but they call the platform instead of the database. Failover updates the standby\'s `dr.role` to `primary`; failback updates the old primary\'s `dr.role` to `standby` and points `dr.uri` at the promoted instance. In both cases the script waits for the resource to report `READY` before repointing traffic.',
      },
      {
        type: 'code',
        label: 'scripts/dr/postgres-failover.sh',
        code: `#!/usr/bin/env bash
set -euo pipefail

: "\${AMETNES_API:?set AMETNES_API}"
: "\${AMETNES_TOKEN:?set AMETNES_TOKEN}"
: "\${PROJECT_ID:?set PROJECT_ID}"
: "\${POSTGRES_STANDBY_ID:?set POSTGRES_STANDBY_ID}"

# 1. Promote through the platform: dr.role standby -> primary.
#    ametnes-set-config.sh wraps the API update shown above.
./scripts/dr/ametnes-set-config.sh \\
  "$PROJECT_ID" "$POSTGRES_STANDBY_ID" 'dr.role=primary'

# 2. Wait for READY and a reachable endpoint, then repoint and verify.
./scripts/dr/ametnes-wait-ready.sh "$PROJECT_ID" "$POSTGRES_STANDBY_ID"
./scripts/dr/repoint.sh postgres standby
./scripts/dr/postgres-smoke-test.sh standby`,
      },
      {
        type: 'code',
        label: 'scripts/dr/postgres-failback.sh',
        code: `#!/usr/bin/env bash
set -euo pipefail

: "\${AMETNES_API:?set AMETNES_API}"
: "\${AMETNES_TOKEN:?set AMETNES_TOKEN}"
: "\${PROJECT_ID:?set PROJECT_ID}"
: "\${POSTGRES_PRIMARY_ID:?set POSTGRES_PRIMARY_ID}"
: "\${POSTGRES_PROMOTED_URI:?set POSTGRES_PROMOTED_URI}"

# 1. Rejoin the old primary as a standby of the promoted instance.
#    The platform resynchronises it; do not send traffic until READY.
#    ametnes-set-config.sh wraps the API update shown above.
./scripts/dr/ametnes-set-config.sh \\
  "$PROJECT_ID" "$POSTGRES_PRIMARY_ID" \\
  'dr.role=standby' "dr.uri=$POSTGRES_PROMOTED_URI"

# 2. Wait for READY and a reachable endpoint, then repoint and verify.
./scripts/dr/ametnes-wait-ready.sh "$PROJECT_ID" "$POSTGRES_PRIMARY_ID"
./scripts/dr/repoint.sh postgres primary
./scripts/dr/postgres-smoke-test.sh primary`,
      },
      {
        type: 'paragraph',
        text: 'Two practical notes. First, the scripts should be idempotent where the technology allows it, and they should fail loudly when a precondition is not met. Second, the pipeline should be run against staging before it is trusted in production. A DR pipeline that has only been reviewed is still an untested runbook.',
      },
      {
        type: 'heading',
        text: 'From a one-off script to a tested capability',
      },
      {
        type: 'paragraph',
        text: 'A single script can configure the RabbitMQ link with one idempotent upstream and one policy; on PostgreSQL, a config change drives the role transition. Production needs the same idea, plus ownership, monitoring, a runbook and a rehearsal calendar. Ametnes deploys and operates open-source data services inside your private environment and handles the day-2 work around them: TLS, secrets, upgrades and the replication plumbing. For RabbitMQ and PostgreSQL, replication and failover are part of that managed surface today. Backup and restore are in development, and coverage is added for each technology that natively supports DR. The platform does not make every technology resilient by default, and it does not pretend to.',
      },
      {
        type: 'paragraph',
        text: 'What stays with your team is the part that cannot be outsourced: deciding when to fail over, approving the action, communicating it, and learning from the rehearsal. The pipeline is the bridge between those decisions and the commands that execute them.',
      },
      {
        type: 'paragraph',
        text: 'Start with one service. Write the runbook. Put it behind a manual, approved gate in a pipeline. Fail it over on a schedule and measure what happens. The first test will be uncomfortable. The fifth will be routine. That is the point: DR is not a document you write once, it is a capability you exercise.',
      },
    ],
    cta: {
      label: 'Talk to us about DR for your open-source stack',
      href: 'https://cloud.ametnes.com/contact/',
      description: 'RabbitMQ, PostgreSQL and the rest of your data layer - replicating, failing over and failing back inside infrastructure you control.',
    },
  },
  {
    slug: 'nigeria-cbn-data-localisation-mandate',
    title: "Nigeria's CBN data localisation mandate is a warning for every global enterprise",
    category: 'Regulatory',
    breadcrumbLabel: 'Regulatory',
    publishedAt: '2026-09-23',
    displayDate: 'September 23, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      "Nigeria's Central Bank has given financial institutions until January 1, 2027 to process, store and manage payment transaction data generated in the country inside Nigeria. It is the clearest signal yet that data residency is becoming a condition of doing business - not a regional edge case.",
    seoDescription:
      "Nigeria's CBN data localisation directive: what it requires, who is in scope, why global enterprises should care, and how in-country processing, storage and disaster recovery change the architecture.",
    blocks: [
      {
        type: 'lead',
        text: "For more than a decade, the default architecture for a global business was simple: put the data in one or two cloud regions, process it wherever the team sits, and let the network hide the distance. Data residency rules are making that assumption expensive. Nigeria's Central Bank has now made it explicit: payment transaction data generated in Nigeria must be processed, stored and managed in Nigeria, and the deadline is January 1, 2027.",
      },
      {
        type: 'heading',
        text: 'What the CBN actually requires',
      },
      {
        type: 'paragraph',
        text: "On June 15, 2026, the Central Bank of Nigeria issued a circular on market structure requirements, data localisation, ultimate beneficial ownership disclosure and systemic oversight in the Nigeria payments system. The data localisation provision is direct: all financial institutions and participants facilitating payments within Nigeria must ensure that payment transaction data generated within Nigeria is stored and managed in Nigeria, in line with applicable Nigerian data protection laws.",
      },
      {
        type: 'paragraph',
        text: "The deadline is January 1, 2027. The scope is payment transaction data - the digital footprint left behind when money moves. That includes payment receipts, transaction logs, account balance histories, card details and personal information linked to Nigerian accounts. It is not a blanket rule over every corporate document, but for any organisation that touches Nigerian payments, it reaches the systems that matter most.",
      },
      {
        type: 'highlight',
        title: 'The directive in brief',
        items: [
          'Issued by the Central Bank of Nigeria on June 15, 2026',
          'Compliance deadline: January 1, 2027',
          'Payment transaction data generated in Nigeria must be processed, stored and managed in Nigeria',
          'Covers licensed financial institutions and participants facilitating payments',
          'Compliance must align with applicable Nigerian data protection law',
          'The obligation flows through partners, vendors and processors as well as direct licensees',
        ],
      },
      {
        type: 'paragraph',
        text: "The CBN circular is the primary source. A plain-language summary of the directive is available in [this analysis of the CBN data localisation mandate](https://nairametrics.com/2026/06/15/cbn-mandates-banks-fintechs-to-store-payment-data-in-nigeria/).",
      },
      {
        type: 'heading',
        text: 'Who is in scope',
      },
      {
        type: 'paragraph',
        text: "Directly, the obligation sits with CBN-licensed institutions: deposit money banks, microfinance banks, mobile money operators, switching companies and payment solution providers. They must ensure that payment transaction data generated in Nigeria is stored and managed locally.",
      },
      {
        type: 'paragraph',
        text: "Indirectly, the reach is wider. Non-licensed fintechs, payment orchestrators, embedded-finance providers and technology vendors that handle payment transaction data are not always named as primary obligors, but they are participants facilitating payments. Licensed partners will flow the requirement down through contracts and vendor audits. For a global platform, that means the obligation can arrive through a customer, a partner or a processor rather than through a local licence.",
      },
      {
        type: 'heading',
        text: 'Why global enterprises should care',
      },
      {
        type: 'paragraph',
        text: "It is tempting to file this under Nigeria. That would be a mistake. Data residency requirements are spreading, and they are arriving through different regulators for different reasons. The EU restricts transfers of personal data outside its boundary. India's central bank has required payment system data to be stored in India. China and Russia impose localisation for certain categories of data. Sector regulators - financial services, health, telecoms, public sector - keep adding their own conditions.",
      },
      {
        type: 'paragraph',
        text: "Residency is where the data sits; sovereignty is whose rules govern it. The two travel together. A global enterprise does not face one residency rule. It faces a patchwork. A single global data plane, one central analytics warehouse, one shared customer profile or one backup region outside the jurisdiction becomes a compliance exception that has to be documented, approved and defended. The more countries you operate in, the less sustainable the centralised model becomes.",
      },
      {
        type: 'highlight',
        title: 'The questions a residency rule forces',
        items: [
          'Where is the data created?',
          'Where is it processed, not just stored?',
          'Where are backups and disaster recovery copies?',
          'Which vendors and subprocessors can access it?',
          'How do you prove location and access to a regulator?',
          'What happens when the local region fails?',
        ],
      },
      {
        type: 'heading',
        text: 'Residency changes the architecture, not just the contract',
      },
      {
        type: 'paragraph',
        text: "A data processing agreement is necessary but not sufficient. If the data is processed by a service in another region, or replicated to a backup outside the country, the contract does not fix the architecture. Residency has to be enforced at the level of the data service: the database, the message broker, the object store, the analytics engine and the backup target all need to live in the permitted location.",
      },
      {
        type: 'paragraph',
        text: "That is an operational problem as much as a legal one. Local infrastructure may be less mature. Power, network and hardware failure modes are different. Latency to a central team increases. Vendor support may be thinner. The answer is not to pretend those risks away; it is to design for them: local replicas, local backups, local DR, observability that works across regions, and a control plane that can operate each location without a human in the middle.",
      },
      {
        type: 'heading',
        text: 'Residency and disaster recovery are the same conversation',
      },
      {
        type: 'paragraph',
        text: "A DR plan that fails over to another country does not satisfy a localisation mandate. If the standby, the backup or the recovery copy sits outside the permitted jurisdiction, the recovery itself creates a compliance event. The standby has to be in-country, or in another jurisdiction the regulator permits, and the failover runbook has to say so.",
      },
      {
        type: 'paragraph',
        text: "That is why we treat disaster recovery as part of the residency design, not a separate project. The same questions repeat: where does the data live, who can reach it, how do you prove it, and what happens when the primary location is unavailable? We wrote about the DR gap in open source - and how to close it for RabbitMQ and PostgreSQL - in [Disaster recovery is where open source gets left out](https://cloud.ametnes.com/blog/open-source-disaster-recovery/).",
      },
      {
        type: 'heading',
        text: 'How Ametnes helps',
      },
      {
        type: 'paragraph',
        text: "Ametnes deploys and operates open-source data services inside infrastructure you control. You register a location - a private data center, a local cloud, a Kubernetes cluster in-country - and deploy databases, brokers, caches, analytics and AI services into it. The data stays in the location you choose; the control plane gives you one way to operate every location.",
      },
      {
        type: 'paragraph',
        text: "That does not make a customer compliant by itself. Compliance depends on the data, the contracts, the controls and the evidence. What the platform does is make residency enforceable in the architecture: the service runs where you put it, TLS is on by default, the API and Terraform provider provision the same way in every location, and day-2 operations - upgrades, patching, certificate rotation - do not require shipping data elsewhere.",
      },
      {
        type: 'highlight',
        title: 'What a residency-ready deployment needs',
        items: [
          'A location in the permitted jurisdiction, not just a region label',
          'In-country processing and storage for the data service itself',
          'Backups and DR copies that stay inside the boundary',
          'Scoped access and audit evidence',
          'A repeatable way to stand up the same stack in each country',
          'A runbook for local failover and failback',
        ],
      },
      {
        type: 'heading',
        text: 'Start before the deadline, not after it',
      },
      {
        type: 'paragraph',
        text: "January 1, 2027 is not far away for a programme that touches data mapping, vendor contracts, architecture and disaster recovery. The organisations that start now will treat it as a design change: map where payment data is created and processed, decide which services must move in-country, build the local landing zone, and rehearse the failover. The ones that wait will treat it as a migration under a regulatory deadline, which is the more expensive way to do the same work.",
      },
      {
        type: 'paragraph',
        text: "If you are working through a residency requirement in Nigeria or anywhere else, we can help you design the local architecture and the DR path that goes with it.",
      },
    ],
    cta: {
      label: 'Talk to us about data residency',
      href: 'https://cloud.ametnes.com/contact/',
      description: 'Design in-country processing, storage and disaster recovery around the services your business depends on.',
    },
  },
  {
    slug: 'open-source-in-your-own-cloud',
    title: 'Open source software in your own cloud, without all the ops overhead',
    category: 'Open Source',
    breadcrumbLabel: 'Open Source',
    publishedAt: '2026-10-10',
    displayDate: 'October 10, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'Running open source in production is mostly operations work - upgrades, TLS, backups, patching - not installation. Ametnes deploys and manages hundreds of open-source data and AI services inside infrastructure you control, with a console, an API and a Terraform provider. Here is the motivation, what developers get, and a free tier that is actually free - no credit card, no trial clock.',
    seoDescription:
      'Why Ametnes exists: deploy and operate open-source data services and AI tools in your own infrastructure, in minutes. The motivation, the developer use case, the self-hosted free tier (no platform licence cost, no credit card), how to register your own cluster as a location, and provisioning everything with the Ametnes Terraform provider.',
    blocks: [
      {
        type: 'lead',
        text: 'Most teams do not choose between open source and a managed cloud because of ideology. They choose the managed option because running open source well - in production, in their own environment - is a surprising amount of work. Ametnes exists to collapse that gap: deploy and operate hundreds of open-source data services and AI tools inside infrastructure you control, with a console, an API and a Terraform provider, in minutes instead of weeks. And you can start on a free tier that never asks for a credit card.',
      },
      {
        type: 'heading',
        text: 'The problem is operations, not installation',
      },
      {
        type: 'paragraph',
        text: 'Standing up a production-ready data service is the easy part. Keeping it there is the job nobody budgets for. You learn the Helm chart layout, the installation values, how the dependencies wire together, persistent storage, gateway and worker configuration, and how to expose the thing externally with TLS, DNS and SSO. That is day 0 and day 1, and depending on how seasoned you are it is a few days of work before anything reaches production.',
      },
      {
        type: 'paragraph',
        text: 'Then the real cost starts. Usage grows and you scale workers. A Log4j-style vulnerability lands and you drop everything to patch. A minor bug forces an upgrade, then a major version. Certificates expire and must be rotated. Backups and restores need testing. None of this is glamorous, all of it is load-bearing, and if it slips you are exposed. The managed alternative has its own tax: your data leaves your boundary, the vendor holds the pricing lever - a public model provider can reprice unilaterally and you read about it after the fact - and workloads woven into a third-party API become expensive to move.',
      },
      {
        type: 'paragraph',
        text: 'So teams end up choosing between two bad options: operate everything yourself, or hand someone else the operation and the relationship. Ametnes is the third option.',
      },
      {
        type: 'highlight',
        title: 'The gap Ametnes fills',
        items: [
          'Open source - no proprietary data plane and no per-seat licensing of the platform itself',
          'Your infrastructure - a VM, private cloud, on-premise, or any public cloud such as AWS, GCP, Azure or Alibaba Cloud',
          'Managed operations - provisioning, upgrades, security patching and TLS rotation handled for you',
          'One control plane - console, API and Terraform provider across every location you register',
        ],
      },
      {
        type: 'heading',
        text: 'What Ametnes is',
      },
      {
        type: 'paragraph',
        text: 'Ametnes is a platform that deploys and manages open source software for you, inside infrastructure you control. It runs on Kubernetes, so it is cloud-agnostic: install the Ametnes agent on a cluster - a laptop with kind, an EKS, GKE or AKS cluster, or your own data center - and register it as a location. From there you deploy from a catalogue of hundreds of solutions without touching a Helm chart.',
      },
      {
        type: 'paragraph',
        text: 'Your data, prompts, embeddings and logs stay within your network boundary. Ametnes operates the day-2 work - version pinning, upgrades, TLS and security patches - so a service stays production-grade after launch, not just at it. Every connection is TLS-secured by default; a plaintext connection is refused.',
      },
      {
        type: 'heading',
        text: 'Built for developers',
      },
      {
        type: 'paragraph',
        text: 'The interface is deliberately small. Everything you can do in the console you can do from the API, and anything you do more than once you can do from Terraform. Locations mean you can run the same service on a laptop, in a private data center, or in a managed cluster, and move when you need to.',
      },
      {
        type: 'highlight',
        title: 'What developers get',
        items: [
          'A real catalogue - relational databases, caches, message brokers, vector databases, AI gateways and observability, not one service wrapped in marketing',
          'Instant, TLS-secured endpoints - connections are encrypted by default and plaintext is rejected',
          'Infrastructure as code - the Ametnes Terraform provider provisions services against the same API you use by hand',
          'Locations anywhere - local workstation, on-premise, or any managed Kubernetes cluster',
          'Day-2 operations included - upgrades, certificate rotation and patching',
          'No lock-in - open-source services in your environment, portable because you own the boundary',
        ],
      },
      {
        type: 'table',
        title: 'A sample of what you can deploy',
        columns: ['Solution', 'Category', 'Typical developer use'],
        rows: [
          ['MySQL / MariaDB / PostgreSQL', 'Relational', 'Application backends, migrations, dev and test databases'],
          ['Redis', 'Cache / queue', 'Sessions, rate limiting, job queues'],
          ['RabbitMQ / Apache Pulsar', 'Messaging / streaming', 'Event-driven services, work queues and high-throughput event streaming'],
          ['Chroma / Weaviate', 'Vector database', 'RAG and semantic search for AI applications'],
          ['LiteLLM', 'AI gateway', 'One OpenAI-compatible API across 100+ model providers'],
          ['Headroom AI', 'LLM context compression', 'Compresses the context an LLM reads - tool output, logs, files, RAG chunks and conversation history - cutting token usage 20-95% without changing answers'],
          ['Dremio / Trino', 'Data lakehouse / query engine', 'Interactive SQL, federated queries and lakehouse analytics'],
          ['Grafana / Metabase', 'Observability / BI', 'Dashboards on top of your own data'],
        ],
        source: 'Representative services from the Ametnes catalogue - many more are available and new ones are added regularly.',
      },
      {
        type: 'heading',
        text: 'The free tier, explained',
      },
      {
        type: 'paragraph',
        text: 'The free tier is not a two-week trial and it does not start a countdown. It is free forever, there is no credit card required, and it follows how Ametnes actually works: you bring your own infrastructure and there is no platform licence cost for eligible services. The service runs in your environment; the only real cost is the infrastructure it runs on.',
      },
      {
        type: 'highlight',
        title: 'What the free tier includes',
        items: [
          '$0 in platform charges, free forever - no credit card required',
          'Bring your own infrastructure - a laptop with kind, a VM, private cloud, on-premise, or any public cloud such as AWS, GCP, Azure or Alibaba Cloud',
          'No platform licence cost for eligible services - for example MySQL, MariaDB, and selected catalogue entries such as Headroom',
          'Self-hosted day-2 operations included - upgrades, TLS rotation and security patching are managed for you',
          'The full platform - console, API and Terraform provider, with TLS-secured connections by default',
          'Membership management and free-tier catalogue services included on every plan, including the free tier',
        ],
      },
      {
        type: 'paragraph',
        text: 'It is aimed at the evaluation and proof-of-concept phase, and at teams who want production-grade open source without handing over their data. When you need more, you move up to the Premium or Enterprise plan and scale by ACU-hour - still in your own infrastructure, with no change to how you work.',
      },
      {
        type: 'heading',
        text: 'Getting started is genuinely a few minutes',
      },
      {
        type: 'paragraph',
        text: 'Ametnes is self-hosted by design, so there is exactly one prerequisite: a Kubernetes cluster you control. A single-node kind cluster on a laptop is enough to start, and the same steps work on EKS, GKE, AKS or your own data center. Install the Ametnes agent to register the cluster as a location, then deploy services into it from the catalogue.',
      },
      {
        type: 'highlight',
        title: 'From zero to a running service',
        items: [
          'Sign up at [cloud.ametnes.com](https://cloud.ametnes.com/console/signup) with an email and password, or with Google, and confirm your email - no credit card required.',
          'Have a Kubernetes cluster ready - kind on your laptop, or EKS, GKE, AKS, or your own data center.',
          'Install the Ametnes agent into the cluster and register it as a location using the same User Supplied Id.',
          'In the console, click `New Resource`, pick a kind from the `Resource Kind` dropdown, and choose your location.',
          'Click `Create`, wait a short while, then copy the connection details - the password is shown once - and connect over TLS.',
        ],
      },
      {
        type: 'code',
        label: 'Install the Ametnes agent and register your cluster as a location',
        code: [
          'helm repo add ametnes https://ametnes.github.io/helm && helm repo update',
          '',
          '# generate a location id, then install the agent into your own cluster',
          'LID=$(uuidgen)',
          'helm upgrade --install --create-namespace --namespace ametnes-system \\',
          '  ametnes-cloud-agent ametnes/cloud-agent \\',
          '  --set agent.config.location=$LID',
          '',
          '# create a Service Location in the console with the same User Supplied Id ($LID)',
          '# once it comes online, deploy services into your own environment',
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'Connect to the service over TLS',
        code: [
          '# use the details shown once on creation:',
          'mysql -h <host> -P <port> -u <user> -D <database> -p',
          '',
          '# verify the connection is encrypted:',
          "mysql> SHOW SESSION STATUS LIKE 'Ssl_cipher';",
        ].join('\n'),
      },
      {
        type: 'heading',
        text: 'Infrastructure as code with Terraform',
      },
      {
        type: 'paragraph',
        text: 'The console is convenient, but infrastructure you cannot reproduce is infrastructure you cannot trust. The Ametnes Terraform provider talks to the same API, so anything you provision by hand you can provision from code - projects, locations and services - and wire into CI so an environment is a commit rather than a runbook.',
      },
      {
        type: 'paragraph',
        text: 'A minimal configuration creates the provider with a token from your account, looks up the project and location, declares the service, and returns its connection details as an output. From there it composes the way Terraform always does: modules, workspaces and reviewable plans.',
      },
      {
        type: 'code',
        label: 'Terraform - provision a service in your own environment',
        code: [
          'terraform {',
          '  required_providers {',
          '    ametnes = { source = "ametnes.com/cloud/ametnes" }',
          '  }',
          '}',
          '',
          'provider "ametnes" {',
          '  token    = var.token',
          '  username = var.username',
          '}',
          '',
          'data "ametnes_location" "location" {',
          '  name = "Demo"',
          '  code = "DSL1"',
          '}',
          '',
          'data "ametnes_project" "project" {',
          '  name = var.project',
          '}',
          '',
          'resource "ametnes_service" "weaviate" {',
          '  name        = "Weaviate-Demo-Instance"',
          '  project     = data.ametnes_project.project.id',
          '  location    = data.ametnes_location.location.id',
          '  kind        = "weaviate:1.19"',
          '  description = "Weaviate Demo Instance"',
          '  capacity { storage = 10 }',
          '  config = {',
          '    "architecture" = "Starter"',
          '  }',
          '}',
          '',
          'output "service_connections" {',
          '  value = ametnes_service.weaviate.connections',
          '}',
        ].join('\n'),
      },
      {
        type: 'heading',
        text: 'Why this matters',
      },
      {
        type: 'paragraph',
        text: 'The bet behind Ametnes is simple: open source has already won the argument on capability, and what remains is operations. If deploying and running that software well becomes a boring, platform-level problem, more teams get to keep their data, control their costs, and avoid re-architecting every time a vendor changes the terms. That is the motivation. The free tier is how we want you to test whether it holds up for you.',
      },
      {
        type: 'paragraph',
        text: 'Sign up, deploy a real database, connect your application, and decide for yourself. No credit card, no sales call, no trial clock.',
      },
    ],
    cta: {
      label: 'Get your free account',
      href: 'https://cloud.ametnes.com/console/signup',
      description: 'Free forever, no credit card required. Deploy your first managed open-source service in minutes.',
    },
  },
  {
    slug: 'ai-regulation-self-hosting-private-ai',
    title: 'AI regulation and self-hosting: why private deployment is an advantage, not a loophole',
    category: 'Regulatory',
    breadcrumbLabel: 'Regulatory',
    publishedAt: '2026-09-18',
    displayDate: 'September 18, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'AI rules are fragmented, extraterritorial and risk-based - and none of them exempt you because your models run on your own hardware. Self-hosting is not a compliance loophole, but it is what makes residency, provenance, auditability and continuity achievable instead of aspirational.',
    seoDescription:
      'The challenges of proposed AI regulation - fragmentation, extraterritoriality, role assignment, risk classification, data provenance and transparency - and how self-hosting and privately operating AI models changes what you can control and prove.',
    blocks: [
      {
        type: 'lead',
        text: 'Every conversation about AI regulation starts with the same wrong question: which law applies? The honest answer is that no single law does. What you are navigating is a fragmented, extraterritorial, risk-based patchwork that overlaps privacy, security and export-control rules - and none of it exempts you because your models run on your own hardware. Self-hosting is not a compliance loophole. It is, however, the thing that makes residency, provenance, auditability and continuity achievable instead of aspirational.',
      },
      {
        type: 'heading',
        text: 'The challenge is fragmentation, not one law',
      },
      {
        type: 'paragraph',
        text: 'There is no single global regime. The EU AI Act takes a risk-based approach and applies extraterritorially, much like GDPR: if your AI systems serve EU users, or their output is used in the EU, you are in scope regardless of where your infrastructure sits. The United States has no unified federal AI statute, but a growing patchwork of state laws and sector regulators - financial services, healthcare, employment - each with their own expectations. China layers algorithm filing, security assessments and content rules on top. The UK remains sector-led, with no single act. A system that satisfies one jurisdiction can violate another, and no single configuration satisfies all of them at once.',
      },
      {
        type: 'paragraph',
        text: 'The timelines move, too. The EU\'s Digital Omnibus package deferred the most demanding high-risk obligations - Annex III systems such as recruitment, credit scoring and law enforcement AI - to December 2027, while GPAI penalty powers and Article 50 transparency duties (chatbot disclosure, synthetic-content marking, deepfake labelling) activate first. The deferral buys preparation time; it does not remove the obligation.',
      },
      {
        type: 'heading',
        text: 'Where the compliance work actually lands',
      },
      {
        type: 'paragraph',
        text: 'Strip away the jurisdictional noise and the same handful of hard problems show up everywhere.',
      },
      {
        type: 'highlight',
        title: 'Four obligations that trip teams up',
        items: [
          'Role assignment - providers, deployers and users carry different duties. Self-host and fine-tune an open-weight model and you may take on provider-level obligations you would otherwise share with a vendor.',
          'Risk classification - "high-risk", "general-purpose" and "systemic risk" are defined by use case, not model. Recruitment, credit, benefits, education and critical infrastructure attract the heaviest duties, and the classification is often the hardest legal question.',
          'Data and provenance - training-data rights, copyright, consent, localization, and records of what data was used where. This is where privacy and AI regimes overlap.',
          'Transparency and oversight - documentation, model cards, logging, disclosure that a user is interacting with AI, synthetic-content marking, and demonstrable human oversight.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Add security and incident-reporting duties, and the pattern is clear: most of the work is evidencing what happened, on what data, under whose authority - not choosing a model.',
      },
      {
        type: 'heading',
        text: 'What self-hosting changes',
      },
      {
        type: 'paragraph',
        text: 'Private operation does not exempt you, but it moves several of these obligations from "ask your vendor" to "configure your platform".',
      },
      {
        type: 'highlight',
        title: 'What private deployment materially helps with',
        items: [
          'Residency and sovereignty - prompts, embeddings, RAG corpora and logs stay in your jurisdiction or air-gapped boundary, without depending on a vendor\'s region map.',
          'Evidence and auditability - because you own the gateway and its logs, you can produce the "who used which model, on what data, with what output" trail regulators ask for.',
          'Provenance and reproducibility - you know and pin the exact weights and version. Silent provider updates cannot invalidate the system you assessed and documented.',
          'Policy as infrastructure - PII redaction, classification-based routing, secrets detection and guardrails enforced once at the gateway rather than per application.',
          'Continuity - access cannot be revoked by a provider\'s policy decision or by geopolitics. The Anthropic Fable 5 suspension, where a government directive cascaded into a global switch-off, is the clearest recent example of why that matters.',
          'Fewer sub-processors - a shorter data-processing chain, clearer accountability, and simpler data-subject access and deletion.',
        ],
      },
      {
        type: 'heading',
        text: 'What self-hosting does not do',
      },
      {
        type: 'paragraph',
        text: 'It is a means, not a certificate. A high-risk system is still high-risk inside your own datacenter, and compliance follows the data, the use case and your role - not the location of the GPUs.',
      },
      {
        type: 'highlight',
        title: 'The traps to avoid',
        items: [
          'Self-hosting does not remove obligations; it shifts the governance work onto you. Risk management, documentation, evaluations, incident response and retention are yours to build.',
          'Open-weight does not mean low-risk. Un-tuned weights may lack safety alignment, and export-control and provenance rules still follow the weights.',
          'Fine-tuning can promote you from deployer to provider, triggering transparency and copyright duties you did not have before.',
          'Security is entirely yours. The blast radius of a private deployment is whatever your boundary, identity and patching allow.',
        ],
      },
      {
        type: 'heading',
        text: 'Mapping obligations to a private AI stack',
      },
      {
        type: 'paragraph',
        text: 'The useful exercise is to map each obligation to a layer you operate, so compliance becomes a property of the platform rather than a per-team checklist.',
      },
      {
        type: 'highlight',
        title: 'From obligation to control',
        items: [
          'Residency - private inference and RAG on approved corpora, with retention and deletion controls.',
          'Transparency and documentation - pinned model versions, model cards, and request and response logging.',
          'Human oversight - gateway policy, approval workflows and scoped access.',
          'Accountability - identity, RBAC, and audited membership and ACL changes.',
          'Security and incident response - guardrails at the gateway, secrets management and a monitored boundary.',
          'Evidence - immutable audit logs and usage attribution by team and use case.',
        ],
      },
      {
        type: 'heading',
        text: 'The bottom line',
      },
      {
        type: 'paragraph',
        text: 'Self-hosting is close to a necessary condition for sovereign, regulated AI: it is what makes residency, provenance, auditability and continuity achievable rather than contractual promises. It is not sufficient. The differentiator is the governance layer you run on top of it - and the test is simple. Can you answer, on demand, which model handled which data, for which user, producing which output, under whose authority? Private GPUs alone will not satisfy a regulator. A governed private platform will.',
      },
    ],
    cta: {
      label: 'Talk to us about regulated AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'headroom-ai-self-host-ametnes',
    title: 'AI OSS spotlight: Headroom AI, and how to self-host it on Ametnes',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-08-02',
    displayDate: 'August 2, 2026',
    cardFooterLabel: 'Read the spotlight',
    excerpt:
      'Headroom AI is an Apache-2.0 context compression layer that strips 20-95% of the tokens your coding agents send - tool output, logs, files, RAG chunks and conversation history - without changing the answers. It wraps the developer tools your teams already use, and Ametnes runs it in your own environment on the free tier.',
    seoDescription:
      'Headroom AI open-source spotlight: benchmark stats, coding-agent integrations, why enterprise rollout needs a proxy layer and careful tuning, and how to self-host Headroom 0.36 on the Ametnes free tier with bring-your-own-infrastructure - plus curl, Python, JavaScript and Java examples.',
    blocks: [
      {
        type: 'lead',
        text: 'Headroom AI is one of those open-source projects that spreads through a developer team in a week. It is an Apache-2.0 context compression layer from [Headroom Labs](https://github.com/headroomlabs-ai/headroom) that sits between your coding agent and the model, compresses everything the agent reads - tool output, logs, files, RAG chunks and conversation history - and sends the model a fraction of the tokens for the same answer. It has passed 73,000 GitHub stars and was Trendshift\'s number one repository of the day, which tells you how much pain it removes. This spotlight covers why it works, the numbers behind it, where it fits in developer workflow, what changes when you roll it out across an enterprise, and how to self-host it on Ametnes - where it is part of the free tier.',
      },
      {
        type: 'heading',
        text: 'Why Headroom is spreading through developer teams',
      },
      {
        type: 'paragraph',
        text: 'The pitch is simple: a large share of what an AI agent reads is structurally redundant. JSON tool responses repeat field names thousands of times, log dumps are 99% noise around the one line that matters, and code exploration returns the same imports and boilerplate over and over. Headroom detects the content type and routes it to the right compressor - SmartCrusher for JSON, an AST-aware CodeCompressor for source code, and the Kompress-v2-base model for prose - then keeps the original locally so the model can retrieve it on demand.',
      },
      {
        type: 'paragraph',
        text: 'None of this is theoretical. Headroom\'s published benchmarks, reproducible from the repository with a fixed seed, show real reductions on the workloads agents hit every day.',
      },
      {
        type: 'highlight',
        title: 'Headroom by the numbers',
        items: [
          '73,000+ GitHub stars and Trendshift #1 repository of the day',
          '90%+ on repetitive JSON and log payloads; far less on dense prose',
          '0.21 ms p50 compression on a 10K-token JSON result - invisible in agent latency',
          'No accuracy tax: GSM8K unchanged at 0.870, TruthfulQA 0.530 → 0.560, and 97% tool-call accuracy at 32% compression',
        ],
      },
      {
        type: 'table',
        title: 'Measured savings on real agent workloads',
        columns: ['Workload', 'Before', 'After', 'Saved'],
        rows: [
          ['Code search (100 results)', '17,199', '13,597', '21%'],
          ['SRE incident debugging', '55,957', '24,340', '57%'],
          ['Codebase exploration', '58,801', '33,895', '42%'],
          ['GitHub issue triage', '46,067', '32,429', '30%'],
        ],
        source: 'Source: Headroom AI published benchmarks, [headroomlabs-ai/headroom](https://github.com/headroomlabs-ai/headroom) (Apache-2.0). Token counts measured with the provider tokenizer and the shipped `compress()`.',
      },
      {
        type: 'heading',
        text: 'The two economics of token compression',
      },
      {
        type: 'paragraph',
        text: 'Token savings mean different things depending on where the model runs - a line on a bill in one case, spare capacity in the other - and Headroom helps in both.',
      },
      {
        type: 'highlight',
        title: 'What the savings turn into',
        items: [
          'Public providers (Claude, OpenAI, DeepSeek) - every input and output token is billed, so compression is a direct cost reduction on the same workload, and smaller prompts also cut latency.',
          'Self-hosted LLMs - there is no per-token bill, but the context\'s KV cache lives in GPU memory. Fewer tokens per request means less VRAM held per conversation, so one GPU serves more concurrent requests and longer contexts.',
          'Throughput, not just cost - shorter prompts cut prefill compute, so time-to-first-token drops and the same hardware handles more requests per minute without buying more GPUs.',
          'Run it in the proxy layer - Headroom\'s compressors run on CPU by default, so putting it in front of the model compresses context before it ever reaches the LLM: the GPU only sees the smaller prompt and never competes for VRAM.',
          'Protect the prefix cache - over-compressing can bust provider prefix caching; Headroom\'s default cache-mode profiles compress only the newest delta to keep those hits.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Either way the outcome is the same shape: you spend less on hosted tokens, and you get more useful work out of the GPUs you already own. That is why the right place for Headroom is the proxy layer - compress once, on the way in, so the model only ever processes the smaller context.',
      },
      {
        type: 'heading',
        text: 'It wraps the tools your developers already use',
      },
      {
        type: 'paragraph',
        text: 'Headroom is not another chat UI to adopt. It is a drop-in layer that wraps the tools already on developer machines. A single `headroom wrap <tool>` command starts a local proxy and configures the agent to route through it - Claude Code, Codex, Cursor, Copilot CLI, VS Code Copilot, Aider, OpenCode, Cline, Continue, Goose, OpenHands, Kimi CLI and more, with `headroom unwrap <tool>` to undo it. Anything that speaks the OpenAI-compatible API works through `headroom proxy`, and any MCP client can call the built-in `headroom_compress`, `headroom_retrieve` and `headroom_stats` tools.',
      },
      {
        type: 'highlight',
        title: 'One compression layer, every entry point',
        items: [
          'Library - call `compress(messages)` inline in Python or TypeScript',
          'Proxy - `headroom proxy --port 8787` with zero code changes, any language',
          'Agent wrap - one command configures Claude Code, Codex, Cursor, Copilot, Aider and others',
          'MCP server - compression and retrieval tools for any MCP-native client',
          'Framework adapters - LiteLLM, LangChain, Agno, Strands, Vercel AI SDK, ASGI middleware',
        ],
      },
      {
        type: 'paragraph',
        text: 'Beyond the prompt, Headroom also trims what the model writes back. Verbosity steering and effort routing cut output tokens - output costs up to 5× input on Opus-class models - and report roughly 32% fewer output tokens with a confidence range rather than a made-up exact figure. Cross-agent memory gives Claude, Codex, Gemini and Grok one shared store with automatic dedup, and `headroom learn` mines failed sessions and writes corrections into `CLAUDE.local.md`, `AGENTS.md` or `GEMINI.md`.',
      },
      {
        type: 'heading',
        text: 'Why enterprise rollout is a different job',
      },
      {
        type: 'paragraph',
        text: 'Everything above is designed for an individual developer on a laptop: run it locally, start saving in minutes, and no prompt or file content leaves the machine to be compressed. Rolling it out across an engineering organisation is a different problem. You need an always-on shared deployment, centralised configuration and version rollout, org-wide savings dashboards, SSO and access control, and a supported path for VPC, on-premise and air-gapped installs. The practical answer is to treat Headroom as part of your proxy layer - the same place you already run routing, guardrails and spend controls - rather than a utility installed on each developer\'s machine.',
      },
      {
        type: 'paragraph',
        text: 'The second half of enterprise success is tuning the compression to your workloads instead of accepting defaults. Headroom ships savings profiles (`coding` by default, plus `balanced`, `agent-90` and `general`), a `HEADROOM_MIN_TOKENS` floor that leaves small or already-dense blocks byte-identical, and live-zone compression that keeps the frozen prompt prefix cache-friendly. Get this right and the savings are predictable; skip it and you either compress too little or cross the line where answer quality starts to move.',
      },
      {
        type: 'heading',
        text: 'Deploying Headroom on Ametnes',
      },
      {
        type: 'paragraph',
        text: 'Headroom is in the Ametnes catalogue as a managed service, and it is part of the Freemium tier, so there is no platform licence cost for the service itself. Ametnes runs on a bring-your-own-infrastructure (BYOI) model: you bring the environment, and the only real cost is the infrastructure Headroom runs on. Deploy it from the console like any other service - pick Headroom 0.36, choose an architecture, choose the location, and Ametnes provisions it in your private network with TLS, upgrades and patching handled.',
      },
      {
        type: 'paragraph',
        text: 'When the service is ready, the console gives you the connection details that matter: the endpoint host and port, and the API key clients use to reach the proxy. That endpoint is the single URL you point your agents, SDKs and CI jobs at. Headroom forwards to whichever model provider you configure behind it, so no prompt or file content leaves your environment to be compressed.',
      },
      {
        type: 'highlight',
        title: 'Why teams start on the free tier',
        items: [
          'Headroom is included in the Ametnes Freemium tier - there is no platform licence cost for the service',
          'BYOI: the only real cost is the infrastructure you run it on',
          'Deployed and operated inside your own VPC, on-premise or air-gapped environment',
          'Managed TLS, version pinning, patching and backups are part of day-2 operations',
        ],
      },
      {
        type: 'heading',
        text: 'Key configuration',
      },
      {
        type: 'paragraph',
        text: 'Configuration splits cleanly in two: what clients need, and what the Headroom service needs. When you point at an Ametnes-hosted endpoint the client surface is deliberately tiny - a base URL, plus the provider key you already use - while the provider, profile and output controls live on the service itself. If you already run LiteLLM as your gateway, you can also register Headroom as a `pre_call` guardrail so every request is compressed before it reaches the upstream provider, without changing gateway config.',
      },
      {
        type: 'highlight',
        title: 'Configuration that matters in production',
        items: [
          'Client: endpoint URL - the `host:port` from the Ametnes service connection details; point every client at it',
          'Client: provider API key - Headroom forwards your real OpenAI or Anthropic key to the provider, so keep using it unchanged',
          'Service: upstream provider - the base URL and key for the model provider Headroom forwards to (Anthropic, OpenAI, Bedrock or a compatible gateway)',
          'Service: compression profile - `HEADROOM_SAVINGS_PROFILE` selects `coding` (default, ~50%), `balanced` (~70%), `agent-90` (~90%) or `general`; `HEADROOM_MIN_TOKENS` leaves small blocks untouched',
          'Service: output shaping - `HEADROOM_OUTPUT_SHAPER=1` trims what the model writes back, proxy-side',
          'Service: retention - CCR keeps originals retrievable locally for a configurable TTL',
        ],
      },
      {
        type: 'paragraph',
        text: 'The tuning switches - provider, profile, output shaping and the anonymous beacon - are read by the proxy process, not by the applications that call it. There is nothing to export on a developer laptop that points at an Ametnes host. When you run `headroom proxy` yourself, they come from that process\'s environment, and a shared proxy hot-syncs changes from `headroom wrap` over `POST /admin/runtime-env`.',
      },
      {
        type: 'code',
        label: 'Client environment - all an app needs to reach Ametnes-hosted Headroom',
        code: [
          'export HEADROOM_URL="https://<host>:<port>"     # from the Ametnes connection details',
          'export OPENAI_API_KEY="<your-openai-key>"       # Headroom forwards this to the provider',
          '',
          '# Ametnes basic auth gates the /stats and /dashboard endpoints only',
          'export HEADROOM_USER="<basic-auth-user>"',
          'export HEADROOM_PASSWORD="<basic-auth-password>"',
        ].join('\n'),
      },
      {
        type: 'title',
        text: 'Integrating into your enterprise workflows',
      },
      {
        type: 'paragraph',
        text: 'Headroom meets your stack where it already is: an OpenAI-compatible proxy for applications, a one-command wrapper for coding agents, and a drop-in endpoint for the business applications your teams already run. Most integrations are a URL change rather than new code, and everything keeps flowing through the endpoint in your environment.',
      },
      {
        type: 'subheading',
        text: 'Integrating into applications',
      },
      {
        type: 'paragraph',
        text: 'For application code you have two options: compress inline with the library, or leave the code untouched and point your existing provider SDK at the Headroom endpoint. The proxy is OpenAI-compatible, so the same pattern works from curl, Python, JavaScript, Java or anything else that can set a base URL.',
      },
      {
        type: 'code',
        label: 'curl - an OpenAI-compatible request through the proxy',
        code: [
          'curl "$HEADROOM_URL/v1/chat/completions" \\',
          '  -H "Authorization: Bearer $OPENAI_API_KEY" \\',
          '  -H "Content-Type: application/json" \\',
          "  -d '{",
          '    "model": "gpt-4o",',
          '    "messages": [',
          '      {"role": "user", "content": "Summarise these logs and find the FATAL line"}',
          '    ]',
          "  }'",
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'Python - compress inline with the library',
        code: [
          'import os',
          '',
          'from headroom import compress',
          'from openai import OpenAI',
          '',
          'messages = [{"role": "user", "content": "Analyze these results"}]',
          'result = compress(messages, model="gpt-4o")',
          '',
          'client = OpenAI()',
          'response = client.chat.completions.create(model="gpt-4o", messages=result.messages)',
          'print(f"Saved {result.tokens_saved} tokens ({result.compression_ratio:.0%})")',
          '',
          '# Proxy-only alternative - skip compress() and point the SDK at Headroom:',
          '# client = OpenAI(base_url=os.environ["HEADROOM_URL"])   # OPENAI_API_KEY is forwarded',
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'JavaScript / TypeScript - library or proxied client',
        code: [
          "import { compress } from 'headroom-ai';",
          '',
          "const messages = [{ role: 'user', content: 'Summarise these logs' }];",
          "const result = await compress(messages, { model: 'gpt-4o' });",
          '',
          '// Or route an OpenAI-compatible client through the proxy:',
          'const client = new OpenAI({',
          '  baseURL: process.env.HEADROOM_URL,',
          '  apiKey: process.env.OPENAI_API_KEY,',
          '});',
          '',
          '// Vercel AI SDK: wrapLanguageModel({ model, middleware: headroomMiddleware() })',
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'Java - any OpenAI-compatible client via the proxy (Java 15+)',
        code: [
          'import java.net.URI;',
          'import java.net.http.HttpClient;',
          'import java.net.http.HttpRequest;',
          'import java.net.http.HttpResponse;',
          '',
          'var body = """',
          '    {',
          '      "model": "gpt-4o",',
          '      "messages": [{"role": "user", "content": "Summarise these logs"}]',
          '    }',
          '    """;',
          '',
          'var request = HttpRequest.newBuilder()',
          '    .uri(URI.create(System.getenv("HEADROOM_URL") + "/v1/chat/completions"))',
          '    .header("Authorization", "Bearer " + System.getenv("OPENAI_API_KEY"))',
          '    .header("Content-Type", "application/json")',
          '    .POST(HttpRequest.BodyPublishers.ofString(body))',
          '    .build();',
          '',
          'var response = HttpClient.newHttpClient()',
          '    .send(request, HttpResponse.BodyHandlers.ofString());',
          '',
          'System.out.println(response.body());',
        ].join('\n'),
      },
      {
        type: 'subheading',
        text: 'Integrating into coding agents',
      },
      {
        type: 'paragraph',
        text: 'Coding agents are where Headroom started, and where it asks the least of you. `headroom wrap <tool>` starts the local proxy, installs Serena for semantic code navigation, and launches the agent already configured to route through Headroom - Claude Code, Codex, Grok CLI, Copilot CLI, VS Code Copilot, Aider, OpenCode, Cline, Continue, Goose, OpenHands and Kimi CLI are all covered, and `headroom unwrap <tool>` restores the settings that existed before. Agents on the same proxy also share one cross-agent memory store with automatic dedup, and `headroom learn` mines failed sessions into your `CLAUDE.local.md`, `AGENTS.md` or `GEMINI.md`.',
      },
      {
        type: 'code',
        label: 'Wrap a coding agent, or install the MCP server',
        code: [
          '# Wrap a coding agent - starts the proxy and configures the tool',
          'headroom wrap claude',
          'headroom wrap codex',
          'headroom wrap cursor',
          '',
          '# Install the MCP server for MCP-native clients',
          'headroom mcp install',
          '',
          '# Undo a durable wrap',
          'headroom unwrap claude',
        ].join('\n'),
      },
      {
        type: 'subheading',
        text: 'Integrating into business applications',
      },
      {
        type: 'paragraph',
        text: 'Any business application that lets you set an OpenAI-compatible base URL can route through Headroom - Open WebUI, LibreChat, Dify, AnythingLLM and similar tools included. Point the app\'s OpenAI connection at the Headroom endpoint and its RAG chunks, long conversations and tool payloads are compressed before they reach the model provider. No application code changes, and the app keeps using the provider key it already has - Headroom forwards it upstream.',
      },
      {
        type: 'code',
        label: 'Open WebUI - set the OpenAI connection to the Headroom endpoint',
        code: [
          '# Open WebUI',
          'OPENAI_API_BASE_URL=$HEADROOM_URL/v1',
          'OPENAI_API_KEY=<your-openai-key>   # forwarded by Headroom to the provider',
          '',
          '# Other OpenAI-compatible apps: set the same base URL in their',
          '# connection settings; the setting name varies by application.',
        ].join('\n'),
      },
      {
        type: 'heading',
        text: 'Verify compression',
      },
      {
        type: 'paragraph',
        text: 'Once traffic is flowing, confirm Headroom is actually saving tokens. The proxy exposes live stats, and Ametnes secures the stats and dashboard endpoints with basic auth - use the credentials from the service connection details. Swap in your own host and port, and only drop the `-u` flag if your deployment is unauthenticated.',
      },
      {
        type: 'code',
        label: 'Check live compression stats',
        code: [
          'curl -s -u "$HEADROOM_USER:$HEADROOM_PASSWORD" \\',
          '  "$HEADROOM_URL/stats" \\',
          "  | jq '{requests: .requests.total, saved: .tokens.saved, pct: .tokens.savings_percent, overhead_ms: .overhead.average_ms}'",
        ].join('\n'),
      },
      {
        type: 'paragraph',
        text: 'A healthy response shows requests and saved tokens climbing as traffic passes through, a savings percentage in the range you expect for the workload, and well under a millisecond of average overhead. If everything reads zero, check that local telemetry is enabled on the service (`HEADROOM_TELEMETRY=on` powers `/stats`, `/metrics` and the dashboard) and that clients are actually hitting the endpoint rather than the provider directly.',
      },
      {
        type: 'paragraph',
        text: 'The same numbers are available as a live UI at `$HEADROOM_URL/dashboard`, with hourly, daily and weekly rollups, per-model and per-client breakdowns, and cost avoided. On a managed Ametnes deployment this is the screen to watch in the first week - it tells you quickly whether the profile you chose fits the workload.',
      },
      {
        type: 'heading',
        text: 'Tuning it for teams, not just laptops',
      },
      {
        type: 'paragraph',
        text: 'Once Headroom is centralised, use it the way platform teams use any shared proxy: pin the version, roll configuration centrally, watch `headroom dashboard` and `headroom savings` for org-wide results, and use `headroom learn --verbosity` to pick a terseness level from real sessions instead of guessing. Start with the balanced profile, measure with a holdout, then tune per workload - high-volume tool traffic, RAG pipelines and coding agents each have a different sweet spot. That is the difference between a demo that saves tokens and a compression layer your engineers trust.',
      },
      {
        type: 'paragraph',
        text: 'Headroom is a rare open-source project that delivers immediate individual value and a clear enterprise return, and it is exactly the kind of workload Ametnes is built to operate: open source, running in your environment, on the free tier, with the day-2 operations handled.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'membership-management',
    title: 'Introducing membership management on the Ametnes platform',
    category: 'Platform',
    breadcrumbLabel: 'Platform',
    publishedAt: '2026-09-14',
    displayDate: 'September 14, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'Invite teammates into your Ametnes workspace, grant scoped access to projects, resources and locations, and manage every membership from one place - with pending invitations, expiry, deny-by-default access and an audit trail.',
    seoDescription:
      'Membership management on the Ametnes platform for solo developers and enterprise organisation accounts: invite members by email, accept or decline invitations, scope access with ACLs across projects, resources and locations, and manage the full membership lifecycle across workspaces.',
    blocks: [
      {
        type: 'lead',
        text: 'Ametnes accounts have always had owners; now they have teams. Membership management is live on the platform. You can invite teammates into your workspace by email, scope exactly what they can see and change, and let them move between the workspaces they belong to - without sharing credentials or building a parallel access model outside the platform.',
      },
      {
        type: 'heading',
        text: 'Every user starts with a workspace',
      },
      {
        type: 'paragraph',
        text: 'When someone signs up for Ametnes they get their own personal workspace, and they are the owner of it. Membership management lets that owner invite other people into the same workspace, and lets each user belong to several workspaces at once - their own, plus any they have been invited to. Privileges are tied to the membership, not to a shared login, so access ends when the membership does.',
      },
      {
        type: 'heading',
        text: 'Invite, accept, decline, leave',
      },
      {
        type: 'paragraph',
        text: 'The owner invites a teammate by email. If that email already has an Ametnes account the invitation is attached to it; if not, the platform creates the user first so the membership always resolves to a real person rather than a dangling invite. The invitation is pending until the recipient accepts or declines it, and pending invitations expire after seven days so stale access does not linger. A revoked or expired membership can be re-issued later instead of piling up duplicates.',
      },
      {
        type: 'highlight',
        title: 'The membership lifecycle',
        items: [
          'Pending - invited, waiting for the recipient to accept or decline (expires after 7 days)',
          'Active - the member belongs to the workspace and can switch into it',
          'Revoked - the member left or was removed; access is withdrawn immediately',
          'Expired - a pending invitation that was never accepted in time',
        ],
      },
      {
        type: 'heading',
        text: 'Access is scoped, and denied by default',
      },
      {
        type: 'paragraph',
        text: 'Membership is not an all-or-nothing switch. Each membership carries a set of access-control entries, and those entries can only target projects, resources and locations - the platform ignores anything else, so a membership can never be used to widen access sideways. A member of a workspace that is not their own is never treated as a root user; they can act only where the membership explicitly grants it, and everything else is denied by default. The owner of the workspace keeps their root status.',
      },
      {
        type: 'highlight',
        title: 'What a membership can grant',
        items: [
          'Projects - the workspace groupings a member can work in',
          'Resources - specific services the member can view or operate',
          'Locations - the data service locations they can deploy into',
        ],
      },
      {
        type: 'heading',
        text: 'What the workspace owner manages',
      },
      {
        type: 'paragraph',
        text: 'Membership management is deliberately owner-only. The workspace owner lists active members, invites new ones, updates a member\'s access as their role changes, and removes them when they leave. Members can accept, decline or leave a workspace themselves, but they cannot remove their own membership on their home account - the workspace you own always stays yours. This keeps a single, accountable path for who gets access and who takes it away.',
      },
      {
        type: 'heading',
        text: 'Every change is audited',
      },
      {
        type: 'paragraph',
        text: 'Invites, acceptances and departures are written to the audit log, so "who added this person, and when did their access end?" has an answer. Combined with revoking access in one place, that turns offboarding into a single action instead of a scavenger hunt across shared logins and scattered credentials.',
      },
      {
        type: 'heading',
        text: 'One model for solo developers and enterprise orgs',
      },
      {
        type: 'paragraph',
        text: 'The same membership model scales from one person to a whole organisation, with no separate "enterprise edition" of access control. A solo developer on the free tier invites their first collaborator with the same invite-and-scope flow an enterprise uses for hundreds of people.',
      },
      {
        type: 'paragraph',
        text: 'For an enterprise, the workspace is the organisation account and memberships are the people in it. Onboarding is an invite with scoped access rather than a shared login; business units and environments can be separated into their own workspaces; the owner stays accountable for every change; and each invite, acceptance and departure is written to the audit log, so offboarding is a single revoke instead of a scavenger hunt.',
      },
      {
        type: 'highlight',
        title: 'Why enterprises standardise on it',
        items: [
          'One organisation account - every member, team and environment under a single governed structure.',
          'Scoped by default - access is granted per project, resource and location, and denied everywhere else.',
          'Accountable ownership - only the workspace owner can invite, update or remove members.',
          'Auditable lifecycle - pending, active, revoked and expired states, with history for every change.',
          'Start free - the same model is available on the free tier, so teams can adopt it before they scale.',
        ],
      },
      {
        type: 'heading',
        text: 'Managing memberships in the console',
      },
      {
        type: 'paragraph',
        text: 'Everything is done in the Ametnes console, so whoever manages your team does not need to touch an API. The flow is short.',
      },
      {
        type: 'highlight',
        title: 'Invite, review and remove members',
        items: [
          'Invite - open the workspace you own, go to `Users`, click `Invite`, enter one or more email addresses, then click `Send Invites`.',
          'Review - new members appear in the `Users` list with a `Pending` status until they respond; use the status filter (All, Pending, Active, Inactive) to see who has joined.',
          'Set access - open a member from the `Users` list and use the `Access Control Lists` tab to grant access to specific projects, resources and locations.',
          'Remove - choose `Remove from workspace` on the member row; access is withdrawn immediately.',
        ],
      },
      {
        type: 'highlight',
        title: 'Accept, decline or leave a workspace',
        items: [
          'Open your own profile in the console.',
          'Go to the `Memberships` tab to see invitations and the workspaces you belong to.',
          'Click `Accept` to join or `Decline` to refuse an invitation.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Membership management is available on every plan, including the free tier, so a solo developer can invite the first collaborator without upgrading and an enterprise can run its whole organisation account on the same model. The full walkthrough - states, scopes, and the console steps - is in the [membership management documentation](/docs/user-guide/platform/membership-management/).',
      },
    ],
    cta: {
      label: 'Create a free workspace',
      href: 'https://cloud.ametnes.com/app/signup',
    },
  },
  {
    slug: 'the-leapfrog-moment-for-emerging-economies',
    title: 'The Leapfrog Moment: Why AI Changes the Math for Emerging Economies',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-07-13',
    displayDate: 'July 13, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'When the UK built GOV.UK it replaced 1,900 government websites and saved billions. AI makes that kind of leapfrog accessible to countries that would never have had that budget before.',
    seoDescription:
      'AI enables leapfrog citizen services for emerging economies - GOV.UK saved billions without AI, and the opportunity is even bigger now. Sovereign AI infrastructure, and why political will matters more than technology.',
    blocks: [
      {
        type: 'lead',
        text: 'AI is one of the biggest accelerants emerging economies have ever had access to - a chance to deliver citizen services that would otherwise have taken decades and billions to build. The cost of building no longer requires the budgets it once did, and the precedent is already there.',
      },
      {
        type: 'heading',
        text: 'The GOV.UK precedent',
      },
      {
        type: 'paragraph',
        text: "Look at GOV.UK. When it launched, it replaced nearly 1,900 separate government websites with a single platform for passports, tax, vehicle registration, education, and more. It launched at less than 30% of the annual cost of the services it replaced, saved taxpayers an estimated £70 million a year, and contributed to more than £4 billion in savings within its first four years.",
      },
      {
        type: 'paragraph',
        text: "That was built over a decade ago, without AI. It's not a stretch to imagine a similar - or better - platform being built today, faster and cheaper, in a country with a fraction of the UK's budget.",
      },
      {
        type: 'heading',
        text: 'The opportunity goes beyond digitizing what already exists',
      },
      {
        type: 'paragraph',
        text: 'Many countries in Africa and parts of the Caribbean still do not have a formal addressing system. Urban planning, road maintenance, waste and drainage management, housing planning, agriculture, education, financial inclusion, healthcare, and pension services all carry the scars of decades of underinvestment and disconnected systems.',
      },
      {
        type: 'paragraph',
        text: 'AI now offers a way to leapfrog - not by copying what wealthier nations built, but by designing something better, natively, without carrying the same technical debt.',
      },
      {
        type: 'heading',
        text: 'The constraint is not technology. It is ambition - but ambition alone is not enough.',
      },
      {
        type: 'paragraph',
        text: 'The real question is whether political leaders have the vision and the will to seize this moment. The tools exist. The cost of building no longer requires the budgets it once did.',
      },
      {
        type: 'paragraph',
        text: 'That said, vision has to be matched with execution, and the non-technical barriers are real. Digital literacy, reliable electricity and internet access, and regulatory frameworks that have not caught up to the technology all still stand in the way. There is also a subtler risk: leapfrogging on AI could mean trading old dependencies for new ones, if the underlying platforms and infrastructure are entirely foreign-owned and foreign-hosted. Political will has to be paired with local capacity to govern, maintain, and eventually own these systems - otherwise the leap forward comes with strings attached.',
      },
      {
        type: 'highlight',
        title: 'What sovereign AI means in practice',
        items: [
          'Models served on infrastructure you govern, behind your network boundary',
          'Policy enforcement at the gateway: PII controls, classification-based routing, and audit logs you control',
          'The ability to keep inference private when geopolitics or regulation makes hosted access unreliable',
        ],
      },
      {
        type: 'paragraph',
        text: 'None of that is a reason for inaction. It is a case for building with intent - sovereignty and capability by design, not an afterthought. There is no longer a credible excuse to point at external forces or historical imperialism as the reason progress is slow. The opportunity is sitting right in front of the countries that choose to act; the work now is doing it right, not just doing it fast.',
      },
      {
        type: 'paragraph',
        text: 'If this resonates, join our upcoming webinar on sovereign AI - we are walking through a practical open-source architecture for governments and regulated industries that need to keep AI infrastructure under their own control. Register for Sovereign AI in Private Environments on 29 July 2026.',
      },
    ],
    cta: {
      label: 'Register for the sovereign AI webinar',
      href: '/events/sovereign-ai-in-private-environments',
    },
  },
  {
    slug: 'litellm-rust-migration-ai-gateway',
    title: 'LiteLLM is moving to Rust',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-06-23',
    displayDate: 'June 23, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'LiteLLM is moving its AI gateway to Rust - welcome news for teams who have been frustrated by its performance under load. Memory spikes, OOM kills, and gateway overhead have made scaling painful.',
    seoDescription:
      'Analysis of LiteLLM migrating its AI gateway to Rust: Python proxy memory and latency limits, benchmark results, staged migration plan, and what it means for teams running private LiteLLM in their own environments.',
    blocks: [
      {
        type: 'lead',
        text: 'On 22 June 2026, LiteLLM published a detailed plan to migrate its AI gateway to Rust - targeting sub-1ms per-request overhead and a sub-100MB deployment footprint. The motivation is not cosmetic. Under real concurrent load, the Python-based proxy peaks around 359MB of memory, adds roughly 7.5ms of gateway overhead per request, and has left production teams fighting OOM kills at the worst possible moment. For Ametnes customers who have long been frustrated by LiteLLM under load, this is genuinely exciting news: the gateway they already run is finally getting a runtime built to scale.',
      },
      {
        type: 'heading',
        text: 'What customers have been running into',
      },
      {
        type: 'paragraph',
        text: 'LiteLLM is the most widely adopted open-source AI gateway for a reason: one OpenAI-compatible API surface across 100+ providers, with routing, fallbacks, rate limits, spend tracking, and guardrails teams can run in their own environment. That model fits regulated and cost-conscious enterprises that want model choice without surrendering control to a SaaS control plane.',
      },
      {
        type: 'paragraph',
        text: 'The friction shows up when traffic scales. Gateway instances that look healthy at low concurrency climb in CPU and resident memory as parallel requests stack up. Teams sizing capacity for a lightweight proxy discover they need several hundred megabytes per instance - and still watch processes restart under burst load. Per-request overhead in the single-digit millisecond range sounds small until you are routing embeddings, classification, or agent tool calls at thousands of requests per second; the gateway becomes a tax on every call.',
      },
      {
        type: 'highlight',
        title: 'Symptoms we see in private AI gateway deployments',
        items: [
          'Memory use that grows with concurrency, forcing larger instances and higher infrastructure cost',
          'OOM kills during traffic spikes - often when scaling has not caught up',
          'Gateway latency overhead that dominates for fast, high-volume workloads like embeddings',
          'More capacity required to hit throughput targets, multiplying memory and licensing cost',
          'Operational toil tuning worker counts and concurrency instead of focusing on model policy',
        ],
      },
      {
        type: 'heading',
        text: 'What LiteLLM measured',
      },
      {
        type: 'paragraph',
        text: 'LiteLLM [published](https://docs.litellm.ai/blog/litellm-rust-launch) the migration plan and benchmark methodology on 22 June 2026. The harness and summarized results are checked into their repository so teams can reproduce the sub-1ms overhead claim independently.',
      },
      {
        type: 'paragraph',
        text: 'In LiteLLM’s published benchmark against the same mock upstream workload, the Rust gateway path served roughly 15× the throughput of the Python proxy (about 6,782 vs 453 requests per second at 50 concurrent clients) while using about 11× less peak memory (about 32MB vs 359MB). Per-request gateway overhead dropped from about 7.5ms on the Python path to about 0.05ms on Rust - well under the sub-1ms target LiteLLM is committing to publicly.',
      },
      {
        type: 'paragraph',
        text: 'Those numbers measure forwarding, transformation, and streaming on the hot path - not full model latency. For workloads where the LLM itself is fast, or where you fan out many small calls, the gateway stops being negligible infrastructure and starts shaping tail latency and fleet size.',
      },
      {
        type: 'heading',
        text: 'What this means if you run LiteLLM privately',
      },
      {
        type: 'paragraph',
        text: 'Teams chose LiteLLM for its provider breadth and enterprise features - the frustration has always been performance under load, not capability. LiteLLM’s Rust migration directly addresses that gap.',
      },
      {
        type: 'paragraph',
        text: 'Multiple Ametnes customers running LiteLLM in private environments had started evaluating a move to Bifrost - not because they wanted to abandon LiteLLM’s provider coverage or operational model, but because the Python proxy could not keep up with their throughput and memory targets. LiteLLM’s Rust announcement changes that calculus: teams can keep the gateway they already know, with the config, integrations, and runbooks they have built, and expect performance in the same class as purpose-built compiled gateways.',
      },
      {
        type: 'paragraph',
        text: 'The Rust work addresses the consequences of running that feature surface on a runtime that was never optimized for high-concurrency, low-overhead forwarding - consequences many of LiteLLM’s own customers reported before this announcement.',
      },
      {
        type: 'heading',
        text: 'Operating the gateway layer in environments you control',
      },
      {
        type: 'paragraph',
        text: 'Ametnes helps teams deploy and operate private AI infrastructure - including LiteLLM and complementary gateways - in VPC, on-premise, and hybrid estates. That means capacity planning, ingress and TLS, upgrades as the Rust migration ships, observability on gateway spend and latency, and day-2 operations so the routing layer stays as reliable as the models behind it.',
      },
      {
        type: 'paragraph',
        text: 'If you are evaluating whether to run LiteLLM at production scale privately, or you are already hitting memory and throughput ceilings on the Python proxy, the migration timeline is worth tracking now - not after the next OOM incident.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI gateways',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'vault-secrets-control-lane',
    title: 'Private secrets management with Vault: a practical guide',
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-06-10',
    displayDate: 'June 10, 2026',
    cardFooterLabel: 'Read the guide',
    excerpt:
      'This guide covers the private secrets management patterns teams adopt first - dynamic database credentials, KV v2 API key rotation, short-lived PKI certificates, and CI integration - with GitLab as one worked example.',
    seoDescription:
      'Practical guide to private secrets management with HashiCorp Vault: dynamic database credentials, KV v2 rotation, short-lived PKI, and CI integration - with GitLab job JWTs as a worked example.',
    blocks: [
      {
        type: 'lead',
        text: 'If your goal is to store a handful of static secrets and inject them into CI jobs, GitLab CI/CD variables are good enough. The gap shows up when you need unique credentials per pipeline run, credentials that expire on their own, internal TLS certificates issued on demand, and audit logs that answer "who read prod/db at 03:12?" This guide walks through the Vault patterns behind GitLab - with commands and pipeline config you can adapt directly.',
      },
      {
        type: 'heading',
        text: 'Prerequisites',
      },
      {
        type: 'paragraph',
        text: 'You need a running Vault cluster with JWT auth, database engine, KV v2, and PKI pre-configured. The examples below use `vault.prod-accounts.ametnes.net` and `sql.prod-accounts.ametnes.net`, and assume GitLab mints job JWTs validated via JWKS at `${GITLAB_URL}/oauth/discovery/keys`. Set your shell variables before running the CLI steps:',
      },
      {
        type: 'code',
        label: 'Environment variables',
        code: `export VAULT_ADDR="https://vault.prod-accounts.ametnes.net"
export VAULT_TOKEN="<admin-token>"          # named admin policy, not root
export GITLAB_URL="https://gitlab.com"
export DEMO_PG_HOST="sql.prod-accounts.ametnes.net"
export DEMO_PROJECT_PATH="acme/platform-api"  # GitLab project path for JWT binding`,
      },
      {
        type: 'heading',
        text: 'Delivery channel vs control plane',
      },
      {
        type: 'paragraph',
        text: 'GitLab is the delivery channel for secrets. Vault is the secrets control plane. You do not rip out GitLab - you wire Vault behind it for what variables cannot do. Most of that capability is open-source Vault: database engine, PKI, KV v2, JWT auth, HA Raft, and audit devices.',
      },
      {
        type: 'highlight',
        title: 'What GitLab variables cannot do',
        items: [
          'Issue a unique database username and password for every CI job',
          'Automatically expire credentials when a job finishes',
          'Mint short-lived internal TLS certificates on demand',
          'Record path-level audit of which identity read which secret',
          'Version and roll back static API keys in one place',
        ],
      },
      {
        type: 'heading',
        text: 'Step 1 - Verify JWT auth (GitLab job tokens)',
      },
      {
        type: 'paragraph',
        text: 'Vault validates GitLab job JWTs against the JWKS endpoint - no static Vault token stored in GitLab project variables. Confirm the auth mount and inspect the role bindings before touching CI:',
      },
      {
        type: 'code',
        label: 'Inspect JWT auth mount',
        code: `vault auth list | grep jwt
vault read auth/jwt/config`,
      },
      {
        type: 'paragraph',
        text: 'Check that `jwks_url` points at `${GITLAB_URL}/oauth/discovery/keys` and `bound_issuer` matches your GitLab base URL. Then inspect the CI-bound role - bindings on `project_path`, `ref`, and `environment` restrict which pipelines can authenticate:',
      },
      {
        type: 'code',
        label: 'Inspect the CI role',
        code: `vault read auth/jwt/role/gitlab-deploy-prod`,
      },
      {
        type: 'paragraph',
        text: 'A short token TTL (10–15 minutes) and a least-privilege policy on the role are the production defaults. For local testing without a pipeline, exchange a job JWT manually:',
      },
      {
        type: 'code',
        label: 'Simulate JWT login from a test pipeline job',
        code: `export DEMO_JWT="<gitlab-job-jwt-from-pipeline>"
vault write -field=token auth/jwt/login role=gitlab-deploy-prod jwt="$DEMO_JWT"`,
      },
      {
        type: 'heading',
        text: 'Step 2 - GitLab CI pipeline',
      },
      {
        type: 'paragraph',
        text: 'Each job declares `id_tokens` so GitLab mints a per-job JWT. The `aud` claim must match what Vault expects - `https://vault.prod-accounts.ametnes.net` in this guide. The job logs in during `before_script`, then fetches secrets at runtime:',
      },
      {
        type: 'code',
        label: '.gitlab-ci.yml',
        code: `stages:
  - deploy

.deploy_vault_auth: &deploy_vault_auth
  id_tokens:
    VAULT_ID_TOKEN:
      aud: https://vault.prod-accounts.ametnes.net   # must match Vault bound_audiences
  before_script:
    - apk add --no-cache jq openssl
    - export VAULT_TOKEN="$(vault write -field=token auth/jwt/login role=gitlab-deploy-prod jwt="$VAULT_ID_TOKEN")"

deploy-staging:
  <<: *deploy_vault_auth
  stage: deploy
  image: hashicorp/vault:1.20
  variables:
    VAULT_ADDR: https://vault.prod-accounts.ametnes.net
  script:
    - vault kv get -field=api_key secret/integrations/demo-saas
    - vault read database/creds/demo-app-readonly
    - |
      vault write -format=json pki/issue/demo-web \\
        common_name="api.prod-accounts.ametnes.net" ttl="24h" alt_names="api.prod-accounts.ametnes.net" \\
        | jq -r '.data.certificate' | openssl x509 -noout -subject -dates`,
      },
      {
        type: 'paragraph',
        text: 'Contrast this with the older pattern: a long-lived `VAULT_TOKEN` or AppRole secret in a protected GitLab variable carries the same blast radius as any static credential. JWT auth gives you per-job identity with nothing stored in project settings.',
      },
      {
        type: 'heading',
        text: 'Step 3 - Dynamic database credentials',
      },
      {
        type: 'paragraph',
        text: 'The database secrets engine issues a unique username and password on every read. Confirm the engine is configured, then request credentials:',
      },
      {
        type: 'code',
        label: 'Read dynamic PostgreSQL credentials',
        code: `vault secrets list | grep database
vault read database/config/demo-postgresql
vault read database/creds/demo-app-readonly`,
      },
      {
        type: 'paragraph',
        text: 'The output includes a unique username (e.g. `v-token-demo-…`), a random password, `lease_id`, and `lease_duration`. Prove they work, then save the lease ID for the revoke demo:',
      },
      {
        type: 'code',
        label: 'Connect with issued credentials',
        code: `export DEMO_DB_USER="<username-from-output>"
export DEMO_DB_PASS="<password-from-output>"
export DEMO_LEASE_ID="<lease_id-from-output>"

psql "postgresql://\${DEMO_DB_USER}:\${DEMO_DB_PASS}@sql.prod-accounts.ametnes.net:5432/appdb?sslmode=disable" \\
  -c "SELECT current_user, now();"`,
      },
      {
        type: 'paragraph',
        text: 'Read again - Vault returns a different username and password. Revoke the first lease and the original credentials stop working immediately:',
      },
      {
        type: 'code',
        label: 'Revoke and verify expiry',
        code: `vault read database/creds/demo-app-readonly   # new creds - not reused
vault lease revoke "$DEMO_LEASE_ID"             # kill the first set
# retry psql with DEMO_DB_USER / DEMO_DB_PASS → connection fails`,
      },
      {
        type: 'paragraph',
        text: 'Rotation is TTL plus revocation, not a quarterly calendar reminder to update `DB_PASSWORD` in GitLab. The same pattern works inline in CI after JWT login:',
      },
      {
        type: 'code',
        label: 'Fetch DB creds inside a deploy job',
        code: `export VAULT_TOKEN="$(vault write -field=token auth/jwt/login role=gitlab-deploy-prod jwt="$VAULT_ID_TOKEN")"
export PGPASSWORD="$(vault read -field=password database/creds/demo-app-readonly)"
# run migrations or deploy using $PGPASSWORD`,
      },
      {
        type: 'heading',
        text: 'Step 4 - API keys in KV v2',
      },
      {
        type: 'paragraph',
        text: 'Third-party API keys cannot be made dynamic at the provider. KV v2 centralizes them with versioning - write, read latest, rotate by writing a new version, and pin a specific version during staged cutover:',
      },
      {
        type: 'code',
        label: 'Write, version, and rotate an API key',
        code: `vault kv put secret/integrations/demo-saas api_key="sk_demo_initial_001"
vault kv get secret/integrations/demo-saas
vault kv metadata get secret/integrations/demo-saas   # version 1

vault kv put secret/integrations/demo-saas api_key="sk_demo_rotated_002"
vault kv get secret/integrations/demo-saas              # latest → version 2
vault kv get -version=1 secret/integrations/demo-saas   # rollback path`,
      },
      {
        type: 'paragraph',
        text: 'Fetch a single field in CI without dumping the whole secret:',
      },
      {
        type: 'code',
        label: 'Read one field from KV in a pipeline',
        code: `vault kv get -field=api_key secret/integrations/demo-saas`,
      },
      {
        type: 'paragraph',
        text: 'Vault does not call Stripe API to invalidate the old key - your automation writes the new version, updates consumers, then revokes at the provider. Vault gives you versioning, access policy, and audit.',
      },
      {
        type: 'heading',
        text: 'Step 5 - PKI: short-lived certificates',
      },
      {
        type: 'paragraph',
        text: 'Vault PKI issues internal certificates with explicit TTL from the same pipeline that fetches database creds and API keys. Confirm the mount, then issue a 24-hour cert:',
      },
      {
        type: 'code',
        label: 'Issue and inspect a short-lived certificate',
        code: `vault secrets list | grep pki
vault list pki/roles

vault write -format=json pki/issue/demo-web \\
  common_name="api.prod-accounts.ametnes.net" \\
  ttl="24h" \\
  alt_names="api.prod-accounts.ametnes.net" \\
  | jq -r '.data.certificate' | openssl x509 -noout -subject -dates`,
      },
      {
        type: 'paragraph',
        text: 'For deploy steps that need files on disk, extract certificate and key from the JSON response:',
      },
      {
        type: 'code',
        label: 'Write TLS files in CI',
        code: `vault write -format=json pki/issue/demo-web \\
  common_name="api.prod-accounts.ametnes.net" ttl=24h > cert.json
jq -r '.data.certificate' cert.json > tls.crt
jq -r '.data.private_key' cert.json > tls.key`,
      },
      {
        type: 'heading',
        text: 'Running Vault reliably in your environment',
      },
      {
        type: 'paragraph',
        text: 'The blocker for many teams is not license cost - it is day-0 and day-2 operations. HA Raft, initialization, ingress, unseal after pod restarts, patching, and backups are real work. Ametnes deploys Vault in your private VPC, on-premise or private datacenter with that foundation handled, including optional transit auto-unseal so a pod restart does not require a Shamir quorum at 3am.',
      },
      {
        type: 'paragraph',
        text: 'OpenBao offers the same engine model under MPL 2.0 if BSL is a procurement concern. Infisical is a credible alternative when project-scoped UX and its own module APIs are enough. Ametnes supports Vault, OpenBao, and Infisical so the choice stays yours.',
      },
    ],
    cta: {
      label: 'Talk to us about private secrets management',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'vercel-breach-private-secrets-lessons',
    title: "Vercel's breach: why production secrets don't belong in shared platforms",
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-05-19',
    displayDate: 'May 19, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'A compromised third-party AI tool and a broad Google Workspace OAuth grant gave attackers a path from corporate identity to Vercel deployment secrets. The incident is a reminder that where you store credentials is a core architecture and compliance decision.',
    seoDescription:
      'Analysis of the April 2026 Vercel security incident: Context.ai OAuth compromise, exposed environment variables, and why teams should run private secrets management with Vault, OpenBao, or Infisical.',
    blocks: [
      {
        type: 'lead',
        text: 'In April 2026, Vercel disclosed unauthorized access to internal systems that began not in its core hosting platform, but in a third-party AI tool (Context.ai) connected to an employee Google Workspace account through OAuth. Attackers used that foothold to pivot into Vercel and read customer environment variables that were not marked as sensitive: API keys, database credentials, tokens, and similar production secrets stored alongside ordinary application config.',
      },
      {
        type: 'heading',
        text: 'How the attack chain worked',
      },
      {
        type: 'paragraph',
        text: 'The incident followed a familiar OAuth supply-chain pattern. Context.ai had been compromised earlier - reportedly through malware on an employee machine - giving attackers access to OAuth tokens for users of Context AI tooling. At least one Vercel employee had authorized Context.ai against their enterprise Google Workspace account with broad permissions. With a stolen token, the attacker impersonated that employee, moved from Google identity into Vercel internal systems, and reached the project settings interface where deployment environment variables are managed.',
      },
      {
        type: 'paragraph',
        text: 'Vercel stores environment variables encrypted at rest, but its sensitive designation is opt-in. Variables not flagged as sensitive remained readable through the management UI - exactly the class of values teams routinely treat as secrets in practice. Vercel advised customers to assume non-sensitive variables may have been exposed and rotate them, enable sensitive classification for secrets going forward, and audit OAuth applications connected to corporate Google Workspace accounts.',
      },
      {
        type: 'highlight',
        title: 'What made this incident different',
        items: [
          'The entry point was a small third-party AI SaaS tool - not a direct breach of the deployment platform',
          'OAuth refresh tokens persisted silently, turning a personal productivity signup into an enterprise lateral-movement path',
          'Production credentials lived in a multi-tenant control plane UI, not a dedicated secrets store with least-privilege access',
          'Classification gaps meant secrets stored as ordinary env vars were readable once an attacker had internal access',
        ],
      },
      {
        type: 'heading',
        text: 'The blast radius of platform-hosted secrets',
      },
      {
        type: 'paragraph',
        text: "Deployment platforms make it easy to colocate secrets with application configuration. That convenience hides a structural risk: your production credentials inherit the security model, access paths, and incident history of someone else's platform. When an attacker reaches the env-var management layer - through phishing, OAuth abuse, insider access, or a vendor compromise - they are one enumeration step away from database URLs, signing keys, and third-party API tokens.",
      },
      {
        type: 'paragraph',
        text: 'The Vercel breach is not an argument against using managed hosting. It is an argument against treating that hosting layer as your secrets vault. Secrets need their own lifecycle: encryption, rotation, audit logging, dynamic credentials, and access policies tied to your identity provider and network boundaries - not a checkbox on a deployment dashboard.',
      },
      {
        type: 'heading',
        text: 'What teams should do now',
      },
      {
        type: 'paragraph',
        text: 'If you run workloads on Vercel or similar platforms, start with inventory and rotation: treat every non-sensitive environment variable that could grant production access as potentially exposed. Enable sensitive classification where the platform supports it, enforce team policies that default new secrets to protected storage, and review OAuth grants across Google Workspace and other identity providers for AI and productivity tools with excessive scopes.',
      },
      {
        type: 'paragraph',
        text: 'Longer term, move production secrets to infrastructure you govern. HashiCorp Vault, OpenBao, and Infisical are built for this job: centralized secret storage, fine-grained policies, rotation workflows, and integration with Kubernetes, CI/CD, and application runtimes - running in your VPC, private cloud, or on-premise environment rather than a shared multi-tenant control plane.',
      },
      {
        type: 'heading',
        text: 'Private secrets management as architecture',
      },
      {
        type: 'paragraph',
        text: 'Where you store secrets is a security decision, not a DevOps convenience. Platform env vars are fine for non-production toggles and public configuration. They are a poor primary home for credentials that unlock databases, payment systems, or customer data - especially when a single OAuth misconfiguration at a vendor can bridge from corporate identity to deployment settings.',
      },
      {
        type: 'paragraph',
        text: 'Ametnes helps teams deploy and operate private secrets infrastructure alongside the rest of their open-source stack: Vault, OpenBao, or Infisical in environments you control, with the day-2 operations - TLS, patching, upgrades, backups, and enterprise support - that make secrets management production-ready rather than a one-off install.',
      },
    ],
  },
  {
    slug: 'ametnes-partners-with-suse',
    title: 'Ametnes partners with SUSE',
    category: 'Partnership',
    publishedAt: '2026-05-17',
    displayDate: 'May 17, 2026',
    partnerName: 'SUSE',
    logoSrc: null,
    excerpt:
      'Ametnes and SUSE align around Rancher so enterprises can run Ametnes-managed open-source solutions on a managed Kubernetes platform in private environments-with full day-2 operations and enterprise support.',
    seoDescription:
      'Ametnes partners with SUSE to deliver managed open-source workloads on Rancher-backed Kubernetes in private infrastructure, with enterprise day-2 operations for stateful services.',
    blocks: [
      {
        type: 'lead',
        text: 'Ametnes has formally partnered with SUSE with a clear focus on Rancher-the Kubernetes management platform enterprises already use to run clusters at scale in private and hybrid environments. Together, the goal is a single path to managed open-source software on Rancher-backed infrastructure, not a patchwork of DIY cluster installs and hand-operated workloads.',
      },
      {
        type: 'heading',
        text: 'Rancher as the foundation, Ametnes on top',
      },
      {
        type: 'paragraph',
        text: 'For the Ametnes platform, Rancher is the relevant SUSE layer: it is how many teams already provision, secure, and operate Kubernetes across data centers and private clouds. This partnership lets customers procure Ametnes-managed open-source solutions running on a managed Rancher Kubernetes platform in their own environments-one operational story from cluster governance through the applications your teams depend on.',
      },
      {
        type: 'paragraph',
        text: 'Platform teams no longer need to treat “who runs Rancher and the clusters” as separate from “who runs Postgres, GitLab, Dremio, or the AI gateway.” Ametnes operationalizes the workload layer on Rancher with the same rigor applied to stateful services that are hard to run in production: persistent storage, controlled upgrades, backup semantics, and consistent access across namespaces and clusters.',
      },
      {
        type: 'highlight',
        title: 'What customers get',
        items: [
          'Managed Rancher Kubernetes in private environments, operated to enterprise standards',
          'Ametnes-managed open-source solutions deployed and run on that Rancher platform',
          'Automated TLS certificate management across services and ingress',
          'Security patching and vulnerability response as part of normal operations',
          'Managed upgrades with tested rollout patterns-not ad-hoc weekend projects',
          'Managed backups for stateful services with recovery aligned to your policies',
          'Enterprise support from Ametnes across the Rancher-backed stack you run privately',
        ],
      },
      {
        type: 'heading',
        text: 'Why Rancher and Ametnes together',
      },
      {
        type: 'paragraph',
        text: 'Rancher gives enterprises a proven control plane for multi-cluster Kubernetes-policy, access, upgrades, and visibility in regulated and security-conscious settings. Ametnes adds the automation-first layer that turns open-source selection into repeatable private deployment: catalog-driven installs, governance, and day-2 management for the solutions you standardize on, all running on the Rancher foundation you already trust.',
      },
      {
        type: 'paragraph',
        text: 'If you are standardizing on Rancher for private or hybrid Kubernetes and want AI-ready open source, data platforms, or core engineering tools without rebuilding operations for every new workload, this partnership is designed to shorten the path from procurement to production.',
      },
    ],
    cta: {
      label: 'Talk to us about on-prem deployments',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'open-weight-models-commoditize-reasoning',
    title: 'Open-weight models are commoditizing reasoning - and that changes everything',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-07-21',
    displayDate: 'July 21, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'Chinese open-weight models - DeepSeek, Qwen, Kimi K3 - now match or beat the most expensive US frontier models on reasoning benchmarks. The premium for thinking is collapsing. For private AI infrastructure, this is the most important trend of 2026.',
    seoDescription:
      'Open-weight models are commoditizing AI reasoning: DeepSeek, Qwen, and Kimi K3 rival GPT-5 and Claude Fable 5 at a fraction of the cost. What this means for private AI infrastructure, procurement, and the enterprise model landscape.',
    blocks: [
      {
        type: 'lead',
        text: 'If you are paying per-token for a frontier reasoning model in July 2026, you are almost certainly overpaying. The gap between open-weight and closed models on reasoning has narrowed so much that the best model for a task - coding, knowledge work, agentic reasoning - might be open-weight, MIT-licensed, and something you can run on your own hardware. The premium that justified $600/month Claude Opus or pay-per-call GPT-5 has been evaporating release by release.',
      },
      {
        type: 'heading',
        text: 'The Chinese model pipeline is changing the landscape',
      },
      {
        type: 'paragraph',
        text: 'The pace from Chinese AI labs over the past 12 months has been relentless. DeepSeek-V3 showed that Mixture-of-Experts could deliver frontier capability at a fraction of the compute budget. Qwen 3 from Alibaba matched GPT-4-class models across coding and multilingual tasks. Then came Moonshot AI\'s Kimi K2 in July 2025 (1 trillion total parameters, 32B active), K2 Thinking in November (outperforming GPT-5 on reasoning), and now Kimi K3 on 16 July - the largest open-weight model ever published at 2.8 trillion parameters, competitive with Claude Fable 5 and GPT-5.6 Sol.',
      },
      {
        type: 'paragraph',
        text: 'K3 releases under a modified MIT license, following K2\'s path. A model that scores well on Humanity\'s Last Exam and SWE-Bench, and runs on commodity hardware with MoE activation, is not a research curiosity. It is a production option procurement teams can budget for in capex instead of opex.',
      },
      {
        type: 'heading',
        text: 'Reasoning as a commodity',
      },
      {
        type: 'paragraph',
        text: 'The term reasoning model once commanded a premium. OpenAI o1 launched at multiples of GPT-4 pricing. Claude Opus carried a $600/month price tag. DeepSeek-R1 changed the conversation by showing that reasoning capabilities could be achieved through reinforcement learning at a fraction of the training cost. K2 Thinking and now K3 have driven the point home - the technique is replicable, and the moat is gone.',
      },
      {
        type: 'paragraph',
        text: 'This is a textbook commoditization cycle. The differentiating capability - multi-step reasoning, chain-of-thought, self-correction - becomes table stakes within 12 to 18 months of the leader\'s release. The premium shifts from what the model can do to where and how you can run it.',
      },
      {
        type: 'highlight',
        title: 'The commoditization timeline',
        items: [
          '2023 - GPT-4 is the only reliable reasoning model. Pricing: $0.03/1K input tokens. No serious open-source alternative.',
          '2024 - Llama 3 405B and Mistral Large close the gap on standard benchmarks. DeepSeek-V2 shows MoE can compete at 1/10th the cost.',
          'H1 2025 - DeepSeek-R1 and Qwen 2.5 match o1 on math and coding. The reasoning premium starts compressing. OpenAI responds with GPT-4.1 price cuts.',
          'H2 2025 - K2 Thinking, Llama 4, and Mistral Large 2 beat GPT-5 on specific benchmarks. Moonshot publishes training methodology. The moat is gone.',
          'July 2026 - K3 competes with frontier models. Open-weight models cover every tier from lightweight coding assistants to frontier reasoning. The question is no longer capability - it is operational fit.',
        ],
      },
      {
        type: 'heading',
        text: 'The take away',
      },
      {
        type: 'paragraph',
        text: 'The take away is straightforward: as model capability becomes a commodity, the differentiator shifts to how you deploy, govern, and operate AI in your own environment. Private AI infrastructure - running models behind your network boundary, with your access controls, audit logs, and governance policies - is where the value moves. Model selection becomes a configuration choice rather than a vendor lock-in, and the teams that have already invested in private deployment infrastructure will be the ones that move fastest.',
      },
      {
        type: 'heading',
        text: 'What this means for enterprise AI procurement',
      },
      {
        type: 'paragraph',
        text: 'When the best model for knowledge-work reasoning is open-weight and MIT-licensed, the buying conversation shifts from which vendor\'s API to connect to, to which model to deploy, on what infrastructure, governed by which policies. The advantage of closed API models - simpler integration, no infrastructure responsibility - is offset by predictable and generally lower total cost of ownership when running open-weight models at sufficient scale.',
      },
      {
        type: 'paragraph',
        text: 'There is a political layer too. The US Treasury threatened sanctions on Chinese AI labs over distillation concerns on 21 July - the same week as K3\'s release. DeepSeek, Moonshot AI, and MiniMax were all accused by Anthropic in February of using fraudulent accounts to extract Claude capabilities. Any organization serving users in multiple jurisdictions needs to weigh model provenance, export controls, and supply-chain continuity alongside capability and price.',
      },
      {
        type: 'heading',
        text: 'The operational question is the strategic question',
      },
      {
        type: 'paragraph',
        text: 'When every team has access to frontier-level reasoning at commodity pricing, the differentiator is not the model - it is the infrastructure. Can you deploy K3 or Qwen 3 behind your own network boundary? Can you govern access, audit usage, control data flow, and swap models without changing application code? Can you maintain the same security posture for an open-weight model that you would for a closed API?',
      },
      {
        type: 'paragraph',
        text: 'That is where private AI infrastructure comes in. Running open-weight models in your VPC, on-prem, or in an air-gapped environment means you get the capability without the vendor dependency. Your inference gateway handles routing, guardrails, and observability. Your governance layer enforces policy regardless of which model is serving a given request. Model selection becomes a configuration change, not a procurement cycle.',
      },
      {
        type: 'paragraph',
        text: 'The race between US and Chinese labs will keep going - new benchmarks, new techniques, new pricing. But the window for treating reasoning capability as a competitive differentiator is closing. The teams that will win are the ones that treat model selection as infrastructure, not vendor management.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'ametnes-partners-with-dremio',
    title: 'Ametnes partners with Dremio',
    category: 'Partnership',
    publishedAt: '2025-11-15',
    displayDate: 'November 15, 2025',
    partnerName: 'Dremio',
    logoSrc: '/media/icons/Dremio.png',
    excerpt:
      'Ametnes is a Dremio Authorized Partner, helping teams run a privately hosted open data lakehouse with governed access to sensitive data and predictable cost-without pushing production datasets into public cloud SaaS.',
    seoDescription:
      'Ametnes partners with Dremio as an Authorized Partner to deliver managed private data lakehouse deployments for healthcare, telecommunications, finance, and other regulated industries.',
    blocks: [
      {
        type: 'lead',
        text: 'On November 15, 2025, Ametnes joined the Dremio Partner Program as an Authorized Partner-formalizing a collaboration focused on one problem enterprise data teams keep running into: they need lakehouse capabilities, but their data cannot leave the environments where security, privacy, and cost control are non-negotiable.',
      },
      {
        type: 'heading',
        text: 'Why a privately hosted data lakehouse',
      },
      {
        type: 'paragraph',
        text: 'Dremio delivers the open data lakehouse experience teams want for SQL analytics, semantic layers, and AI-ready data access. Ametnes operationalizes it inside your private cloud or on-premises estate so analysts and engineers connect to production data where it already lives-without standing up a parallel copy in a multi-tenant SaaS analytics product.',
      },
      {
        type: 'paragraph',
        text: 'That matters when ingestion and egress to cloud SaaS platforms become a material line item, when data residency rules block external processing, or when security teams require consistent identity, audit, and network boundaries around every query path. Keeping the lakehouse private preserves those controls while still giving teams self-service velocity.',
      },
      {
        type: 'highlight',
        title: 'Where teams see the strongest fit',
        items: [
          'Healthcare - clinical and operational datasets that must stay inside approved environments while still supporting research and analytics workflows',
          'Telecommunications - network, subscriber, and service data with strict isolation and high operational expectations on the platform underneath',
          'Financial services - trading, risk, and customer data where governance, retention, and cost predictability outweigh convenience of public-cloud analytics tiers',
        ],
      },
      {
        type: 'heading',
        text: 'Cost, security, and operations together',
      },
      {
        type: 'paragraph',
        text: 'Moving large volumes of sensitive data into cloud SaaS for analytics often introduces recurring ingest charges, cross-region transfer costs, and duplicated storage-on top of the compliance review each new export path requires. A privately hosted lakehouse on Ametnes lets you colocate compute with data, apply consistent access policy, and scale capacity on infrastructure you already operate.',
      },
      {
        type: 'paragraph',
        text: 'Through the partnership, Ametnes deploys and runs Dremio alongside the rest of your open-source stack with the same day-2 discipline: TLS management, patching, upgrades, backups for stateful components, and enterprise support-so the lakehouse is a managed platform service, not a one-off integration project.',
      },
      {
        type: 'paragraph',
        text: 'Together, the combination gives data and platform leaders a practical procurement path: Dremio for lakehouse capabilities, Ametnes for private deployment and ongoing operations, and your teams for models, semantic definitions, and governance rules that stay under your control.',
      },
    ],
    cta: {
      label: 'Explore Dremio on Ametnes',
      href: '/solutions/service-dremio',
    },
  },
  {
    slug: 'metabase-breach-private-deployment',
    title: "The Metabase breach: why your analytics layer doesn't belong in a shared environment",
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-08-15',
    displayDate: 'August 15, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'Metabase disclosed a 0-day attack on its cloud and patched a vulnerability affecting self-hosted instances from version 58 up. For Ametnes customers, the fix is a version bump - in the UI or one line in Terraform.',
    seoDescription:
      "The 6 August 2026 Metabase security incident explained: the reset_password 0-day, affected versions and minimum safe releases, and how Ametnes customers upgrade Metabase in a private, customer-controlled environment.",
    blocks: [
      {
        type: 'lead',
        text: "On 6 August 2026, Metabase disclosed a security incident affecting both its cloud and self-hosted users. An attacker exploited an unknown ('0-day') vulnerability in Metabase version 58 and above against Metabase Cloud. Metabase immediately blocked the endpoints used in the attack, identified and patched the vulnerability, and published a security advisory alongside minimum safe releases for every affected version. For Ametnes customers, this is a version-upgrade problem, and it is one you can fix in minutes without exposing your analytics layer to someone else's timeline.",
      },
      {
        type: 'heading',
        text: 'What happened',
      },
      {
        type: 'paragraph',
        text: "Metabase Cloud was attacked using a 0-day vulnerability in Metabase versions 58 and above. Metabase responded by blocking the endpoints used in the attack, then identifying and patching the vulnerability. Cloud customers were upgraded automatically. The open question - and the one that matters most here - is self-hosted instances.",
      },
      {
        type: 'paragraph',
        text: "A self-hosted Metabase on an affected version remains vulnerable. After gaining access to your instance, the attacker can inject arbitrary SQL against the Metabase application database, which can give them administrator access. From there they can change your application configuration, steal stored credentials for your connected databases, read any data accessible through those connections, and export it. In practice, the blast radius is every database your Metabase can reach.",
      },
      {
        type: 'heading',
        text: 'The vulnerability and how to spot an attack',
      },
      {
        type: 'paragraph',
        text: "The affected endpoint is `/api/session/reset_password`. Metabase's advisory flags the following pattern in application or ingress logs as a strong signal that an instance has been compromised:",
      },
      {
        type: 'code',
        label: 'Attack pattern in your logs',
        code: `POST /api/session/reset_password  -> 400
GET  /api/user/current            -> 200`,
      },
      {
        type: 'paragraph',
        text: "If you see that pair, treat the instance as potentially compromised and follow the remediation steps below.",
      },
      {
        type: 'heading',
        text: 'Affected versions and minimum safe releases',
      },
      {
        type: 'paragraph',
        text: "Metabase version 58 and above is affected; versions below 58 are not vulnerable. If you are running a point release below the minimum safe release for your version, you are still vulnerable and should upgrade. The minimum safe releases are:",
      },
      {
        type: 'highlight',
        title: 'Minimum safe releases per version',
        items: [
          '0.58.24 - Docker image: metabase/metabase:v0.58.24',
          '0.59.21 - Docker image: metabase/metabase:v0.59.21',
          '0.60.17 - Docker image: metabase/metabase:v0.60.17',
          '0.61.11 - Docker image: metabase/metabase:v0.61.11',
          '0.62.9 - Docker image: metabase/metabase:v0.62.9',
          '0.63.5 - Docker image: metabase/metabase:v0.63.5',
        ],
      },
      {
        type: 'heading',
        text: 'What self-hosters should do after upgrading',
      },
      {
        type: 'paragraph',
        text: "Metabase's advisory lists the immediate steps for anyone whose `/api/session/reset_password` endpoint was publicly reachable: revoke all active user sessions by deleting rows in the `core_session` table, review and remove unrecognized API keys, review administrator accounts for unexpected changes, rotate credentials for any connected databases, and review data-warehouse and query logs for unauthorized activity. If you cannot upgrade right away, block the `/api/session/reset_password` endpoint as a temporary workaround.",
      },
      {
        type: 'heading',
        text: 'Upgrading Metabase on Ametnes',
      },
      {
        type: 'paragraph',
        text: "The first line of defence is to run Metabase in a customer-controlled environment that is only accessible to you. On the Ametnes platform, Metabase is deployed inside your private environment and is only reachable within your own internal network - no shared tenancy, no public control plane, and no data leaving your perimeter.",
      },
      {
        type: 'paragraph',
        text: "With that foundation in place, upgrading should be a matter of selecting the newer version and clicking Update in the UI. The platform provisions Metabase as a private, customer-controlled resource, so the patch ships as a version change you approve on your own schedule - not a forced maintenance window on a shared platform.",
      },
      {
        type: 'paragraph',
        text: "If you manage Metabase with the Ametnes Terraform provider, you do not orchestrate the upgrade yourself. Simply update the `kind` with the newer version and the Ametnes agent performs all the necessary orchestration to upgrade - pulling the new image and rolling it out - inside your environment.",
      },
      {
        type: 'code',
        label: 'main.tf - bump the Metabase version',
        code: `resource "ametnes_service" "metabase" {
  name     = "analytics"
  project  = data.ametnes_project.default.id
  location = data.ametnes_location.primary.id

  # Update this line to the latest safe point release, then apply.
  kind = "metabase:0.63.5"

  spec = {
    visibility = "public"
    config = {
      architecture = "starter"
    }
  }
}`,
      },
      {
        type: 'paragraph',
        text: "Everything stays inside your private controlled environment - your data, your credentials, and the upgrade path itself.",
      },
      {
        type: 'paragraph',
        text: "If you have not yet provisioned Metabase privately, follow the step-by-step [provisioning tutorial](https://cloud.ametnes.com/docs/tutorials/metabase/) to deploy it in your own environment and connect it to your data.",
      },
      {
        type: 'heading',
        text: 'The lesson: control the layer, not just the tool',
      },
      {
        type: 'paragraph',
        text: "Metabase is a capable tool, and this incident is not a reason to abandon it. It is a reason to run it somewhere you can patch it on your own terms. In a private, customer-controlled environment you set the security posture, you audit access, and when a vulnerability like this lands, you upgrade without waiting on a vendor's incident response or exposing your instance to a shared control plane.",
      },
      {
        type: 'paragraph',
        text: "Your dashboards, your data, your perimeter - and now your upgrade, too.",
      },
    ],
    cta: {
      label: 'Talk to us about private Metabase',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'deepseek-price-increase-public-ai',
    title: 'DeepSeek raises prices: the hidden cost of renting your AI',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-08-23',
    displayDate: 'August 23, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      "DeepSeek - the low-cost leader that reset the market's expectations of what AI should cost - has raised its prices. It is clear, public AI pricing is a decision you do not get to make.",
    seoDescription:
      "Analysis of DeepSeek's price increase and why it matters for teams building on public AI APIs: one-sided pricing, hidden lock-in, and the case for running open-weight models on private infrastructure you control.",
    blocks: [
      {
        type: 'lead',
        text: 'DeepSeek built its reputation on being cheap - so cheap that it forced the entire frontier market to reprice. So when DeepSeek announces a price increase, the instinct is to shrug it off as a correction. This is a reminder that when you rent your AI from a public provider, pricing is a decision you never get to make. The number on the invoice can change under you, and there is nothing in your contract that protects you from it.',
      },
      {
        type: 'heading',
        text: 'The company that reset the market',
      },
      {
        type: 'paragraph',
        text: 'DeepSeek changed the conversation in late 2024 and early 2025. V3 delivered frontier-class capability at a fraction of the compute budget, and R1 showed that reasoning could be built with reinforcement learning at a fraction of the cost of the closed labs. Almost overnight, the assumption that a reasoning model had to carry a premium collapsed. OpenAI and Anthropic responded with cuts of their own, and teams began budgeting for AI as if cheap was the new permanent baseline.',
      },
      {
        type: 'paragraph',
        text: "That baseline was never guaranteed. DeepSeek's pricing was an aggressive, capital-subsidised land grab - not a promise. Now it has begun to unwind, and the reaction tells you more than the price table does.",
      },
      {
        type: 'heading',
        text: 'The repricing no customer voted on',
      },
      {
        type: 'paragraph',
        text: 'DeepSeek has announced an increase to its API pricing. The specifics matter less than the mechanics: the decision was made unilaterally, it applies to everyone running against the same endpoint, and it lands on the same invoice that was sold as the cheap option. There is no negotiation, no grandfathering for your workloads, and no guarantee it will not happen again.',
      },
      {
        type: 'highlight',
        title: 'What you inherit when you build on a public provider',
        items: [
          'Pricing is a one-sided decision - you read about it when it is already done',
          'Terms of service can shift underneath you without your sign-off',
          'Workloads and data woven into a third-party API are costly to move',
          "Your unit economics are hostage to someone else's roadmap",
        ],
      },
      {
        type: 'heading',
        text: 'The cost compounds quietly',
      },
      {
        type: 'paragraph',
        text: 'Teams that integrated DeepSeek - or any public model - directly into their products now face a re-architecture decision; absorb the increase, renegotiate nothing, or rip out and re-plumb the integration. Each option carries cost, and none of them was on the roadmap when the cheap integration was chosen.',
      },
      {
        type: 'paragraph',
        text: "This is the pattern, not the exception. A price change you did not sign off on, terms that shift under you, and workloads you cannot move because they are woven into a third-party API. What looked like a simple integration decision becomes a standing dependency on someone else's pricing roadmap.",
      },
      {
        type: 'heading',
        text: 'Run open-weight models where you control the costs',
      },
      {
        type: 'paragraph',
        text: "The antidote is not to avoid DeepSeek - or any single vendor - it is to stop renting the model by the token in a way that hands someone else the pricing lever. Open-weight models can be served on infrastructure you control, behind your network boundary, with your own access controls and audit logs. Once the model runs in your environment, a vendor's price change is no longer a line item on your invoice - it is someone else's problem.",
      },
      {
        type: 'paragraph',
        text: "Private deployment changes the equation in three ways; predictable, capital-based cost instead of a per-token meter that can be repriced at will; no dependency on a third party's pricing roadmap; and full control over where your data and workloads live. The models - including DeepSeek's own open weights - are the commodity. The control is the differentiator.",
      },
      {
        type: 'paragraph',
        text: 'Ametnes helps teams deploy and operate private AI infrastructure - including open-weight models and the gateways in front of them - in VPC, on-premise, and hybrid estates. That means capacity planning, ingress and TLS, observability on spend and latency, and day-2 operations so the serving layer stays as reliable as the models behind it.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
];

export const getPostBySlug = (slug) => blogPosts.find((post) => post.slug === slug);

export const getSortedPosts = () =>
  [...blogPosts].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
